<!--
  ColorwayEditor.vue -- map an embroidery design's stops to orderable threads.

  Card: Embroidery colorway editing (MUST-SHIP) https://trello.com/c/2hcgDM7J

  Upload a .emb -> the design's colorways are listed -> pick one -> each stop
  shows its color, the thread the design was authored with, and the nearest
  ORDERABLE Madeira thread. Choosing threads re-renders a debounced preview.

  Three behaviours here exist because of things measured against the live
  Wilcom API on 2026-08-14, not because of a spec:

  1. THE CURRENT COLORWAY IS SELECTED BY DEFAULT, not index 0. Real designs
     disagree about which one is current (T&L.EMB -> Colorway 1;
     EddieBauer.EMB and SIR.EMB -> Colorway 2), so defaulting to the first
     would show the wrong colorway on most designs.

  2. NO PREVIEW UNTIL A THREAD IS CHOSEN. Rendering a NAMED colorway without
     a <recolor> thread payload returns a pale ghost outline from Wilcom --
     the same failure long attributed only to colorwaytype="all". A ghost of
     the user's own logo as their first impression reads as a broken product,
     so the preview waits for real thread data. See wilcom_emb_render's
     docstring for the three-render proof.

  3. AN APPROXIMATE SUGGESTION IS VISIBLY APPROXIMATE, and a FAR one asks for
     a human. Most stops in a real design carry brand="Default" -- a raw
     color, not a thread anybody chose. Distance is the tell: ~5.91 means the
     design named a real thread; ~233.87 means nobody did. Presenting the
     second as a confident match would put an unfulfillable code on an order.
-->
<template>
    <section class="cwe" data-testid="colorway-editor">
        <header class="cwe__head">
            <!-- This route is a SIBLING of library_artwork_detail, not a child,
                 so it inherits no breadcrumb or layout back-link -- the page is
                 structurally an orphan and has to carry its own way out.
                 Omitted entirely for a standalone upload: a back link with
                 nowhere to go is worse than none. -->
            <button
                v-if="effectiveArtworkId"
                type="button"
                class="cwe__back"
                data-testid="colorway-back-to-artwork"
                @click="backToArtwork"
            >
                &lsaquo; Back to decoration
            </button>
            <div class="cwe__title-row">
                <h2 class="cwe__title">Thread colorways</h2>
                <HelpIcon
                    audience="owner"
                    slug="embroidery-colorways"
                    heading="pick-the-colorway-you-are-editing"
                />
            </div>
            <p class="cwe__sub">
                Map each stop in an embroidery file to a real Madeira thread.
                Saved colorways can be reused across stores.
            </p>
        </header>

        <div class="cwe__upload">
            <label class="cwe__upload-label" for="cwe-file">
                <v-icon aria-hidden="true">upload_file</v-icon>
                {{ colorways.length ? 'Use a different design' : 'Choose a .emb or .dst file' }}
            </label>
            <input
                id="cwe-file"
                class="cwe__upload-input"
                type="file"
                accept=".emb,.dst"
                data-testid="colorway-file-input"
                @change="onFileChange"
            />
            <span v-if="filename || sourceName" class="cwe__filename">
                {{ filename || sourceName }}
                <span v-if="fromArtwork && !filename" class="cwe__muted"
                    >(from this artwork)</span
                >
            </span>
        </div>

        <p
            v-if="error"
            class="cwe__error"
            role="alert"
            data-testid="colorway-editor-error"
        >
            {{ error }}
        </p>

        <p
            v-if="loading"
            class="cwe__loading"
            data-testid="colorway-editor-loading"
        >
            Reading colorways...
        </p>

        <!-- Three DIFFERENT empty states, because "nothing here" has three
             different causes and the same sentence for all of them misleads.
             A .dst artwork DOES have a design; saying "no design loaded"
             reads as a load failure the user should retry. -->
        <p
            v-if="!colorways.length && !loading && !error"
            class="cwe__empty"
            data-testid="colorway-editor-empty"
        >
            {{ emptyMessage }}
        </p>

        <!-- TWO COLUMNS (Jake, 2026-09-01): "I'd rather the save, default,
             and saved colorways in a column to the right of the embroidery
             stage."

             Header and upload stay full-width ABOVE this grid; only the
             working area splits. The STAGE column keeps the colorway
             chooser, the per-stop thread table and the preview -- Jake was
             explicit that per-stop editing stays with the stage, so the
             right column carries only the three things he named.

             WHY THIS FIXES A REAL PROBLEM AND IS NOT JUST TASTE: measured on
             8089 at 1280x720, the save panel sat at y=672 with the stops
             table running to y=659, so on a 720px-tall viewport every save
             control was below the fold. The list of saved colorways -- the
             thing you consult while deciding what to save -- was furthest
             from the stage it describes.

             Collapses to one column under 1100px, where two columns would
             leave neither usable. -->
        <div v-if="colorways.length" class="cwe__body">
            <div class="cwe__stage">
            <!-- Colorway chooser -->
            <ul class="cwe__list" data-testid="colorway-list">
                <li
                    v-for="cw in colorways"
                    :key="cw.name"
                    class="cwe__list-item"
                    :class="{ 'cwe__list-item--on': cw.name === selectedName }"
                    :data-testid="`colorway-option-${cw.name}`"
                    @click="selectColorway(cw.name)"
                >
                    <span class="cwe__swatches" aria-hidden="true">
                        <span
                            v-for="(t, i) in cw.threads.slice(0, 8)"
                            :key="i"
                            class="cwe__swatch"
                            :style="{ background: t.hex || '#ccc' }"
                        />
                    </span>
                    <span class="cwe__list-name">{{ cw.name }}</span>
                    <span
                        v-if="cw.current"
                        class="cwe__badge"
                        :data-testid="`colorway-current-${cw.name}`"
                        >Current</span
                    >
                </li>
            </ul>

            <!-- Discoverability instruction (Jake P1, 2026-08-20 live testing:
                 "the buttons aren't discoverable at all. We at least need to
                 provide some instruction for that.") The stop-number button in
                 column 1 isolates that stop in the preview, but nothing said
                 so. One sentence beats a tooltip nobody hovers; the `title`
                 attribute on the button itself stays as a second, redundant
                 signal for anyone who does hover. -->
            <p
                v-if="selected"
                class="cwe__sub cwe__stop-instruction"
                data-testid="colorway-stop-instruction"
            >
                Click a stop number to isolate that stop in the preview.
            </p>

            <!-- WHICH saved colorway is on the stops (Jake, 2026-10-21: "when
                 I go back into the colorway editor for a given decoration,
                 load the default colorway (first saved)"). Without this line
                 a reloaded colorway and Wilcom's own suggestions look
                 identical, which is exactly how "my colorway was not saved"
                 got reported. The id rides in the testid so a scenario can
                 name the row it expects. -->
            <p
                v-if="activeSavedColorway"
                class="cwe__sub cwe__loaded-saved"
                :data-testid="`colorway-loaded-saved-${activeSavedColorway.id}`"
            >
                Showing saved colorway
                <strong>{{ activeSavedColorway.name }}</strong>
            </p>

            <!-- Stops for the selected colorway.

                 ROWS ARE STOPS, NOT PALETTE SLOTS. A Wilcom colorway carries a
                 fixed-size thread palette padded with a stock default set, so
                 iterating `threads` renders colors the machine never sews --
                 it showed 15 rows for a 1-color design and asked for 14 thread
                 choices that go nowhere. `stops` is what gets stitched; each
                 one indexes into the palette. See _parse_stop_sequence in
                 api/src/endpoints/wilcom_preview.py. -->
            <table v-if="selected" class="cwe__stops">
                <thead>
                    <tr>
                        <th>Stop</th>
                        <th class="cwe__th-stitches">Stitches</th>
                        <th>Design color</th>
                        <th>Thread</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(stop, idx) in stopRows"
                        :key="idx"
                        :class="{ 'cwe__stop-row--hot': highlightedStop === idx }"
                        :data-testid="`colorway-stop-row-${idx}`"
                    >
                        <td class="cwe__stop-n">
                            <!-- A BUTTON, not a press-and-hold on the number
                                 (Jake, explicitly). Mike's prototype used
                                 pointer-hold; a button is discoverable,
                                 keyboard-reachable, and does not fight touch
                                 scrolling. Stop numbers are 1-based because
                                 that is what an operator reads off the
                                 machine. -->
                            <button
                                type="button"
                                class="cwe__highlight"
                                :class="{
                                    'cwe__highlight--on': highlightedStop === idx,
                                }"
                                :aria-pressed="highlightedStop === idx"
                                :aria-label="`Isolate stop ${stop.stop_index ?? idx + 1} in the preview`"
                                :title="
                                    highlightedStop === idx
                                        ? 'Stop isolated in the preview - click to show all'
                                        : 'Isolate this stop in the preview'
                                "
                                :data-testid="`colorway-stop-highlight-${idx}`"
                                @click="toggleHighlight(idx)"
                            >
                                <span :data-testid="`colorway-stop-n-${idx}`">{{
                                    stop.stop_index ?? idx + 1
                                }}</span>
                            </button>
                        </td>
                        <td
                            class="cwe__stitches"
                            :data-testid="`colorway-stop-stitches-${idx}`"
                        >
                            {{ formatStitches(stop.num_stitches) }}
                        </td>
                        <td>
                            <span
                                class="cwe__chip"
                                :style="{ background: stop.hex || '#ccc' }"
                                aria-hidden="true"
                            />
                            <span class="cwe__stop-desc">
                                {{ stop.description || stop.hex }}
                            </span>
                            <!-- The plain word for the color. A 56-stop table
                                 is unscannable by hex; the family is how a
                                 human refers to it. The hex and Madeira code
                                 stay authoritative right beside it. -->
                            <span
                                v-if="stop.family"
                                class="cwe__muted cwe__family"
                                :data-testid="`colorway-stop-family-${idx}`"
                            >
                                {{ stop.family }}
                            </span>
                            <!-- Leading space is deliberate: without it the
                                 badge abuts the description ("Blueno thread
                                 in file"). -->
                            <span
                                v-if="isUnchosen(stop)"
                                class="cwe__muted cwe__unauthored"
                                :data-testid="`colorway-stop-unauthored-${idx}`"
                            >
                                no thread in file
                            </span>
                        </td>
                        <!-- EDIT either field; the other is derived. -->
                        <td class="cwe__edit">
                            <span
                                class="cwe__chip"
                                :style="{
                                    background:
                                        stopState(idx).hex ||
                                        stop.suggested?.hex ||
                                        '#ccc',
                                }"
                                aria-hidden="true"
                            />
                            <input
                                class="cwe__input cwe__input--hex"
                                type="text"
                                placeholder="hex"
                                :value="stopState(idx).hex"
                                :data-testid="`colorway-stop-hex-${idx}`"
                                :aria-label="`Stop ${idx + 1} color hex`"
                                @input="editStop(idx, 'hex', $event.target.value)"
                                @blur="blurStop(idx)"
                            />
                            <input
                                class="cwe__input cwe__input--code"
                                type="text"
                                placeholder="code"
                                :value="stopState(idx).madeira"
                                :data-testid="`colorway-stop-madeira-${idx}`"
                                :aria-label="`Stop ${idx + 1} Madeira code`"
                                @input="
                                    editStop(idx, 'madeira', $event.target.value)
                                "
                            />
                            <span
                                v-if="stopError(idx)"
                                class="cwe__needs"
                                :data-testid="`colorway-stop-error-${idx}`"
                                >{{ stopError(idx) }}</span
                            >
                            <span
                                v-else-if="stopState(idx).unknownCode"
                                class="cwe__needs"
                                :data-testid="`colorway-stop-unknown-${idx}`"
                                >not in {{ chart }}</span
                            >
                            <!-- Approximate applies BOTH before and after an
                                 edit: an untouched stop shows whether its
                                 SUGGESTION was a guess, an edited one shows
                                 whether the resolver's link was. Keying this
                                 only on edit state hid the flag on exactly
                                 the stops nobody has looked at yet. -->
                            <span
                                v-else-if="
                                    stopState(idx).touched
                                        ? stopState(idx).approx
                                        : stop.suggested && !stop.suggested.exact
                                "
                                class="cwe__approx"
                                :data-testid="`colorway-stop-approx-${idx}`"
                                >closest match</span
                            >
                            <span
                                v-if="needsChoice(stop) && !stopState(idx).touched"
                                class="cwe__needs"
                                :data-testid="`colorway-stop-needs-choice-${idx}`"
                                >pick a thread</span
                            >
                            <!-- Confirm is GONE (Jake, 2026-08-20): suggestions
                                 are already auto-applied, so a button whose
                                 only job was turning our guess into the
                                 user's decision was busywork. The "closest
                                 match" pill above is what stays -- it is the
                                 only remaining signal a guessed thread was
                                 never a deliberate choice. -->
                            <!-- The way BACK to the suggestion, once a row no
                                 longer matches it (typed over, or picked from
                                 the catalogue). An always-visible control that
                                 sometimes does nothing is worse than none, so
                                 this only appears once the row has actually
                                 diverged. Restores BOTH the thread and the
                                 guessed-pill state -- reverting makes it a
                                 guess again, because it is. -->
                            <span class="cwe__actions">
                                <button
                                    v-if="canRevert(idx)"
                                    type="button"
                                    class="cwe__action cwe__revert"
                                    :data-testid="`colorway-stop-revert-${idx}`"
                                    :title="`Restore the closest-match thread for stop ${idx + 1}`"
                                    @click="revertStop(idx)"
                                >
                                    Use closest match
                                </button>
                                <!-- The way OUT of the two text fields. Typing a
                                     code presumes you already know one; a
                                     decorator handed a brand color ("our blue is
                                     2101 C") or only an intent ("a warm red") has
                                     no code to type. -->
                                <button
                                    type="button"
                                    class="cwe__action cwe__browse"
                                    :data-testid="`colorway-stop-browse-${idx}`"
                                    @click="openPicker(idx)"
                                >
                                    Pick color
                                </button>
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- Save / load named colorways.

                 Only when we know the artwork VERSION: saved colorways hang
                 off the version (ADR 0044, "snapshot not live link"), so a
                 design with no version at all has nothing to attach to.
                 Offering a control that would 404 is worse than not showing
                 it -- but Bug 1 (Jake, live testing) showed that a SILENTLY
                 absent control is also worse: it read as "saving is broken"
                 for a full day. So the reason below (saveUnavailableReason)
                 covers the one case that still has nothing to attach to
                 (a design never opened from an artwork at all); every other
                 case is the artwork's own design, whose read carries its
                 version id. (Choosing a different file at an artwork route
                 mints a new version first, then re-reads it --
                 confirmReplaceFile.) -->
            <!-- Preview. Deliberately absent until threads exist (see 2
                 above). Lives in the STAGE column: it is a picture of the
                 design, not a save control. -->
            <figure v-if="previewUrl" class="cwe__preview">
                <!-- No ink-hex: a recolor changes the ink, so the source
                     artwork's sampled color would be wrong. The preview is
                     sampled on its own. -->
                <DecorationArtThumb
                    :src="previewUrl"
                    alt="Colorway preview"
                    data-testid="colorway-preview-img"
                />
                <figcaption class="cwe__muted">
                    Preview updates a moment after you change a thread.
                </figcaption>
            </figure>
            <p
                v-else-if="previewBusy"
                class="cwe__muted"
                data-testid="colorway-preview-pending"
            >
                Rendering preview...
            </p>
            </div>
            <!-- THE SIDE COLUMN. Save, applies-to, and the saved list --
                 exactly the three controls Jake named, and nothing else. -->
            <aside class="cwe__side" data-testid="colorway-side-column">
            <div
                v-if="artworkVersionId"
                class="cwe__saved"
                data-testid="colorway-saved-panel"
            >
                <div class="cwe__saved-row">
                    <input
                        v-model="saveName"
                        class="cwe__input cwe__input--name"
                        type="text"
                        placeholder="Name this colorway"
                        data-testid="saved-colorway-name"
                        aria-label="Colorway name"
                    />
                    <button
                        type="button"
                        class="cwe__save"
                        :disabled="saving"
                        data-testid="saved-colorway-save"
                        @click="saveColorway"
                    >
                        {{ saveButtonLabel }}
                    </button>
                </div>
                <!-- SUBSTRATE DECLARATION (Jake, 2026-08-25: "a colorway
                     should be able to be marked as for dark or for light.
                     This way you can set it once and get the correct(ish)
                     default behavior in the product editor even though we
                     don't know in the deco context what colors are
                     available.").

                     A decoration cannot know product colours -- it is reused
                     across many products in many stores. But light/dark is
                     ABSTRACT, so it can be declared here, and the product
                     editor (which DOES know the real garment colours) reads
                     it as a DEFAULT it may override.

                     THREE MUTUALLY-EXCLUSIVE OPTIONS, exactly one lit.
                     "All colors" used to have no button, because null meant
                     "unanswered" and a button would have implied the user
                     owed a decision. Multi-save inverted that (Jake,
                     2026-08-29): with several colorways per version, "which
                     is the default for ALL colours" is a role one of them
                     has to hold, so it must be selectable. 'All' still
                     posts NULL -- the wire format did not change. -->
                <div class="cwe__substrate" data-testid="colorway-substrate-control">
                    <!-- "Applies to", NOT "Default for" (Jake, 2026-09-01).
                         "Default for" promises exclusivity -- one default
                         per substrate -- which is exactly the constraint he
                         ruled out. The label has to stop claiming a
                         uniqueness the model does not have and never did. -->
                    <span id="cwe-substrate-label" class="cwe__substrate-label"
                        >Applies to</span
                    >
                    <!-- ONE SEGMENTED CONTROL, ONE ROW (Jake, 2026-10-21: the
                         All/Light/Dark buttons wrapped onto several lines).
                         A 3-column grid cannot wrap by construction, and the
                         short labels ("All", "Light", "Dark") are what let
                         three segments fit a narrow column; the long form
                         stays in the title/aria-label. -->
                    <div
                        class="cwe__segmented"
                        role="group"
                        aria-label="Applies to"
                        data-testid="colorway-substrate-segmented"
                    >
                        <button
                            v-for="opt in SUBSTRATE_OPTIONS"
                            :key="substrateKey(opt)"
                            type="button"
                            class="cwe__substrate-opt"
                            :class="{
                                'cwe__substrate-opt--on': substrateTarget === opt.value,
                            }"
                            :aria-pressed="substrateTarget === opt.value ? 'true' : 'false'"
                            :aria-label="opt.label"
                            :title="opt.label"
                            :data-testid="`colorway-substrate-${substrateKey(opt)}`"
                            @click="selectSubstrate(opt.value)"
                        >
                            <!-- v-if on the MODEL, not a CSS class, so a test
                                 (and a screenshot) can tell chosen from
                                 unchosen. A class-only marker reads as
                                 present either way. -->
                            <span
                                v-if="substrateTarget === opt.value"
                                class="cwe__substrate-check"
                                :data-testid="`colorway-substrate-selected-${substrateKey(opt)}`"
                                aria-hidden="true"
                                >&#10003;</span
                            >{{ opt.short }}
                        </button>
                    </div>
                </div>
                <p
                    v-if="saveError"
                    class="cwe__error"
                    role="alert"
                    data-testid="saved-colorway-error"
                >
                    {{ saveError }}
                </p>
                <!-- DUPLICATE NAME -> ASK, don't refuse (Jake, 2026-09-01):
                     "Saving a colorway whose name already exists currently
                     errors. I want a dialog asking whether to replace it."

                     Names match CASE-INSENSITIVELY, so this is also what a
                     user sees when they save 'Navy' over 'navy' -- and
                     confirming keeps the NEW casing, per Jake's ruling.

                     Inline rather than a modal overlay: the question is
                     about the field directly above it, and the answer is one
                     of two buttons. A full modal would be heavier than the
                     decision it carries. -->
                <div
                    v-if="replacePrompt"
                    class="cwe__replace"
                    role="alertdialog"
                    aria-live="polite"
                    data-testid="saved-colorway-replace-confirm"
                >
                    <p class="cwe__replace-q">
                        A colorway named
                        <strong>{{ replacePrompt.existingName }}</strong>
                        already exists. Replace it with
                        <strong>{{ replacePrompt.name }}</strong>?
                    </p>
                    <div class="cwe__replace-actions">
                        <button
                            type="button"
                            class="cwe__save"
                            :disabled="saving"
                            data-testid="saved-colorway-replace-accept"
                            @click="confirmReplace"
                        >
                            {{ saving ? 'Replacing...' : 'Replace' }}
                        </button>
                        <button
                            type="button"
                            class="cwe__replace-cancel"
                            data-testid="saved-colorway-replace-cancel"
                            @click="cancelReplace"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
                <!-- EVERY saved colorway, not just the newest (Jake,
                     2026-08-29, reversing his 2026-08-21 "keep one saved per
                     version"): "What we really need is the ability to save
                     more than one colorway, and choose which one is the
                     default for all vs light vs dark. We also need the option
                     to delete colorways."

                     The backend always stored N (unique on version+name), so
                     restoring the list was a template change, exactly as the
                     2026-08-21 note predicted. Each row carries its
                     default-for, so "which one is the default for X" is
                     answerable by looking. -->
                <div
                    v-if="savedColorways.length"
                    class="cwe__saved-list"
                    data-testid="saved-colorway-list"
                >
                    <div
                        v-for="cw in savedColorways"
                        :key="cw.id"
                        class="cwe__saved-item"
                        :class="{
                            'cwe__saved-item--active': activeSavedId === cw.id,
                        }"
                        :data-testid="`saved-colorway-${cw.id}`"
                    >
                        <!-- Two lines per row so nothing is squeezed: line 1 is WHAT
                             it is (name + applies-to), line 2 is what it
                             looks like (thread strip) and what you can do
                             with it (Load / Delete). -->
                        <div class="cwe__saved-top">
                            <span
                                class="cwe__saved-name"
                                :title="cw.name"
                                >{{ cw.name }}</span
                            >
                            <button
                                type="button"
                                class="cwe__saved-target"
                                :class="substrateClass(cw.substrate_target)"
                                :disabled="reclassifyingId === cw.id"
                                :data-testid="`saved-colorway-target-${cw.id}`"
                                :title="`Applies to ${substrateLabel(
                                    cw.substrate_target,
                                )} -- click to change`"
                                @click="cycleSubstrate(cw)"
                            >
                                {{ substrateLabel(cw.substrate_target) }}
                            </button>
                        </div>
                        <div class="cwe__saved-bottom">
                            <span
                                class="cwe__saved-swatches"
                                aria-hidden="true"
                                :data-testid="`saved-colorway-swatches-${cw.id}`"
                            >
                                <span
                                    v-for="(t, i) in (cw.threads || []).slice(0, 12)"
                                    :key="i"
                                    class="cwe__swatch"
                                    data-testid="saved-colorway-swatch"
                                    :style="{ background: t.hex || '#ccc' }"
                                />
                            </span>
                            <span class="cwe__saved-actions">
                                <button
                                    type="button"
                                    class="cwe__saved-load"
                                    :aria-label="`Load colorway ${cw.name}`"
                                    :data-testid="`saved-colorway-load-${cw.id}`"
                                    @click="loadSavedColorway(cw)"
                                >
                                    Load
                                </button>
                                <!-- THE ONLY PLACE COLORWAYS GET DELETED. The server
                                     refuses (409) when variants are still bound, and
                                     that refusal is surfaced in the error line above
                                     rather than swallowed. -->
                                <button
                                    type="button"
                                    class="cwe__saved-delete"
                                    :disabled="deletingId === cw.id"
                                    :aria-label="`Delete colorway ${cw.name}`"
                                    :data-testid="`saved-colorway-delete-${cw.id}`"
                                    @click="deleteColorway(cw)"
                                >
                                    {{ deletingId === cw.id ? 'Deleting...' : 'Delete' }}
                                </button>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- The one remaining case with nothing to save against: a
                 design never opened from an artwork at all. A NAMED reason,
                 not a silently missing panel -- see the block above. -->
            <p
                v-else-if="saveUnavailableReason"
                class="cwe__muted cwe__save-unavailable"
                data-testid="colorway-save-unavailable-reason"
            >
                {{ saveUnavailableReason }}
            </p>
            </aside>
        </div>

        <!-- Thread picker, as a RIGHT-SIDE FLYOVER (Jake, 2026-08-20: "it
             should be a flyover on the right sort of like the store preview
             in the store editor"). Mirrors StoreEditShell.vue's
             `.ces-preview-*` drawer: a fixed scrim with click-to-close, a
             right-anchored panel that transforms in, a head row carrying the
             title and close control. Fixed/z-indexed siblings of `.cwe`
             rather than nested inside it, same reason as the reference: a
             drawer positioned relative to a scrolled-away ancestor would not
             stay pinned to the viewport.

             `pickerStop` stays the SINGLE open/target variable (unchanged) --
             one variable so open-state and target cannot disagree and apply
             a pick to the wrong row. The flyover only changes how that state
             is PRESENTED. -->
        <div
            class="cwe__picker-scrim"
            :class="{ open: pickerStop !== null }"
            data-testid="colorway-picker-scrim"
            @click="pickerStop = null"
        ></div>
        <aside
            v-if="pickerStop !== null"
            class="cwe__picker-drawer"
            :class="{ open: pickerStop !== null }"
            role="dialog"
            aria-modal="true"
            :aria-label="`Thread for stop ${stopRows[pickerStop]?.stop_index ?? pickerStop + 1}`"
            data-testid="colorway-picker-drawer"
            @keydown.esc.stop="pickerStop = null"
        >
            <header class="cwe__picker-head">
                <span
                    >Thread for stop
                    {{ stopRows[pickerStop]?.stop_index ?? pickerStop + 1 }}</span
                >
                <button
                    type="button"
                    class="cwe__picker-close"
                    data-testid="colorway-picker-close"
                    @click="pickerStop = null"
                >
                    Close
                </button>
            </header>
            <div class="cwe__picker-body" data-testid="colorway-picker-panel">
                <ThreadColorPicker :chart="chart" @select="onPickThread" />
            </div>
        </aside>

        <!-- A DIFFERENT FILE IS A NEW VERSION (Jake, 2026-10-21): "Before
             doing any of this, we need a modal dialog asking the user if it's
             okay to make the destructive change, and notifying that there is
             no Undo for this." Nothing is uploaded or saved until Confirm;
             Cancel leaves the decoration exactly as it was. The copy states
             what the versioned save actually does -- the saved colorways
             stop applying, but nothing is deleted: products and the old
             approvals keep the current file. -->
        <div
            v-if="pendingReplaceFile"
            class="cwe__modal-scrim"
            data-testid="colorway-replace-file-scrim"
            @click.self="cancelReplaceFile"
        >
            <div
                ref="replaceFileDialog"
                class="cwe__modal"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="cwe-replace-file-title"
                data-testid="colorway-replace-file-dialog"
                @keydown.esc.stop="cancelReplaceFile"
                @keydown.tab="trapReplaceDialogFocus"
            >
                <h3 id="cwe-replace-file-title" class="cwe__modal-title">
                    Replace this decoration's file?
                </h3>
                <p class="cwe__modal-lead">
                    <strong data-testid="colorway-replace-file-name">{{
                        pendingReplaceFile.name
                    }}</strong>
                    becomes this decoration's current file.
                </p>
                <ul class="cwe__modal-list">
                    <li>
                        Saved colorways for the current file will not apply to
                        the new one. You start with none.
                    </li>
                    <li>
                        Products that already use this decoration keep the
                        previous file.
                    </li>
                    <li>
                        Client approvals must be given again for the new file.
                    </li>
                </ul>
                <p class="cwe__modal-warn">There is no Undo for this change.</p>
                <p
                    v-if="replaceFileError"
                    class="cwe__error"
                    role="alert"
                    data-testid="colorway-replace-file-error"
                >
                    {{ replaceFileError }}
                </p>
                <div class="cwe__modal-actions">
                    <button
                        ref="replaceFileCancel"
                        type="button"
                        class="cwe__replace-cancel"
                        :disabled="replacingFile"
                        data-testid="colorway-replace-file-cancel"
                        @click="cancelReplaceFile"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="cwe__save"
                        :disabled="replacingFile"
                        data-testid="colorway-replace-file-confirm"
                        @click="confirmReplaceFile"
                    >
                        {{ replacingFile ? 'Replacing...' : 'Replace file' }}
                    </button>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
// The SHARED api client, whose baseURL already carries the /api prefix and
// the auth interceptor. A relative `/api/...` on `this.$axios` hits the
// FRONTEND host instead and silently returns the SPA's index.html -- which
// looks like "no data" rather than an error. Verified on 8083.
import api from '@/services/api'
import { formatArtworkName } from '@/utils/formatArtworkName'
import { SUPPRESS_ERROR_TOAST } from '@/interceptors/authResponseInterceptor'
import {
    ARTWORK_UPLOAD_ERROR_EMPTY,
    isEmptyFile,
} from '@/utils/artworkUploadContract'
import { buildImageUrl } from '@/utils/imageBaseUrl'
import HelpIcon from '@/components/Help/HelpIcon.vue'
import ThreadColorPicker from './ThreadColorPicker.vue'
import { usableSavedThreads } from './savedColorwayThreads'
import DecorationArtThumb from '@/components/decoration/DecorationArtThumb.vue'

// One preview per settled edit, not per click. The vendor call is cheap but
// not free, and an unbounded per-keystroke fan-out to a third party is the
// pattern that caused a self-inflicted SanMar outage.
const PREVIEW_DEBOUNCE_MS = 600

// Above this redmean distance the "nearest" thread is not a real answer --
// the design almost certainly carried no thread choice for that stop, so a
// human has to pick. 5.91 was a genuine Madeira match in the live data;
// 233.87 was a raw brand="Default" color. 60 sits well clear of both.
const NEEDS_CHOICE_DISTANCE = 60

// Per-stop debounce for the hex <-> Madeira link. Short enough to feel live
// while typing, long enough that a 6-character hex is ONE resolve, not six.
const RESOLVE_DEBOUNCE_MS = 180

// The three "default for" choices. 'light' / 'dark' match the backend
// vocabulary exactly -- artwork_colorways.substrate_target,
// decorations.substrate_target and vendor_color_hex.substrate_bucket all
// spell it the same way.
//
// NULL IS IN THIS LIST NOW, as an explicit "All colors" option (Jake,
// 2026-08-29: "choose which one is the default for all vs light vs dark").
//
// This REVERSES the earlier design, where NULL was the absence of an answer
// and rendering a button for it would have made not-answering look like a
// decision the user owed. Multi-save is what changed it: with several
// colorways on one version, "which is the default for ALL colours" is a role
// some colorway has to hold, so it has to be selectable.
//
// The WIRE FORMAT IS UNCHANGED -- 'all' is `value: null` and posts as null.
// The DB CHECK allows exactly NULL/light/dark, so a literal 'all' string
// would 400. Only the vocabulary moved, not the storage.
const SUBSTRATE_OPTIONS = [
    { value: null, key: 'all', label: 'All colors', short: 'All' },
    { value: 'light', label: 'Light garments', short: 'Light' },
    { value: 'dark', label: 'Dark garments', short: 'Dark' },
]

export default {
    name: 'ColorwayEditor',

    components: { DecorationArtThumb, HelpIcon, ThreadColorPicker },

    props: {
        // Reserved for persisting a colorway against an artwork. Not used for
        // reads: the editor works off an upload so it needs no schema change.
        // NAME MUST MATCH THE ROUTE PARAM. The route uses `props: true`, which
        // passes params through VERBATIM -- Vue does NOT convert the
        // snake_case `artwork_id` param into a camelCase `artworkId` prop. It
        // was declared as `artworkId` and therefore sat null forever, so the
        // editor silently never auto-loaded. Accept both: the route supplies
        // artwork_id, a parent mounting this directly may pass artworkId.
        artwork_id: { type: [Number, String], default: null },
        artworkId: { type: [Number, String], default: null },
        chart: { type: String, default: 'polyneon' },
    },

    data() {
        return {
            filename: '',
            // Which design is loaded, and whether it came from the artwork
            // itself or a manual upload. Shown so the user knows what they
            // are editing rather than guessing.
            sourceName: '',
            fromArtwork: false,
            // Why THIS artwork cannot have colorways (server's words), when
            // that is the reason the table is empty.
            unsupportedReason: '',
            // Per-stop validation messages, keyed by stop index. NOT the
            // page-level `error` banner: a bad value in one row must name that
            // row, and must not survive the row becoming valid.
            stopErrors: {},
            colorways: [],
            design: null,
            selectedName: null,
            chosen: {},
            // Which stop is isolated in the preview (index into stopRows), or
            // null for the whole design.
            highlightedStop: null,
            // The preview to RESTORE when a highlight is cleared -- the last
            // render that was not a highlight (the recolor preview, or ''
            // before any thread was chosen). Captured lazily on the FIRST
            // highlight click so it always holds the real "whole design"
            // image, never a highlighted one.
            _preHighlightPreviewUrl: null,
            // Per-(colorway, stop) render cache: {`${colorwayName}::${stopIndex}`: imageUrl}.
            // Re-clicking a stop already seen this session is FREE -- Jake
            // explicitly accepted one vendor render per highlight click, but
            // a 56-stop design must not cost 56 renders every time someone
            // re-browses the same stops. Cleared whenever the colorway
            // changes, since the palette (and therefore the render) differs
            // per colorway -- a cached image from Colorway 1 must never be
            // shown as Colorway 2's highlight.
            _highlightCache: {},
            highlightBusy: false,
            // The artwork VERSION being edited -- what a saved colorway hangs
            // off. Comes from the read route's `source`.
            artworkVersionId: null,
            savedColorways: [],
            // The saved colorway currently applied to the stops, or null.
            // Set by loadSavedColorway (by hand or on entry) and cleared by
            // anything that replaces the stops' design or colorway.
            activeSavedId: null,
            saveName: '',
            saving: false,
            saveError: '',
            // The pending replace question, or null. An OBJECT rather than a
            // boolean because confirming needs the colliding row's id (which
            // the 409 supplies) plus the name and threads the user was
            // saving -- rebuilding those at confirm time would re-read state
            // the user may have changed while the dialog was open.
            replacePrompt: null,
            // Which saved colorway is mid-reclassify, or null. An ID, not a
            // boolean, for the same reason as deletingId: a shared flag
            // would disable every pill at once and read as "the list broke".
            reclassifyingId: null,
            // The colorway id currently being deleted, or null. An ID rather
            // than a boolean so the button that was clicked can show its own
            // busy state -- a shared boolean would disable every row's
            // Delete at once, which reads as "the whole list broke".
            deletingId: null,
            // 'light' | 'dark' | null. NULL IS THE INITIAL STATE AND A REAL
            // ONE -- "not declared" is a different fact from "declared
            // light", and the product editor treats it differently: an
            // absent declaration lets it fall back to the garment colours it
            // actually knows, where a wrong one overrides that knowledge.
            substrateTarget: null,
            // Which stop the thread picker is open FOR, or null for closed.
            // One variable rather than an `open` flag plus a target index --
            // two variables can disagree, and the failure mode of that
            // disagreement is applying a picked thread to the wrong stop.
            pickerStop: null,
            // Per-stop edit state: what the user typed plus what the resolver
            // derived. Keyed by stop index.
            edits: {},
            previewUrl: '',
            previewBusy: false,
            loading: false,
            error: '',
            _designFile: null,
            // The file the user chose at an artwork route, awaiting the
            // replace confirmation (see the dialog), or null. The File itself
            // rather than a flag: confirming must upload exactly the file the
            // dialog named.
            pendingReplaceFile: null,
            replacingFile: false,
            replaceFileError: '',
            // Bumped ONLY when the user chooses a file (handleFile ->
            // _resetForNewDesign). loadFromArtwork does NOT bump it; it
            // captures it, so an artwork read that lands after a file was
            // chosen is recognised as stale. Every async answer about a
            // design -- a render, a highlight, the upload parse, the artwork
            // read -- compares its captured epoch and is dropped if stale.
            // Without it a slow render of the OLD file could land after a
            // swap and sit under the new file's stops.
            designEpoch: 0,
            // Per-render sequence: of two renders of the SAME design, only
            // the newest may write the preview.
            previewSeq: 0,
            _previewTimer: null,
            _resolveTimers: {},
            _resolveSeq: {},
        }
    },

    computed: {
        // Where "Back" goes when another surface sent the user here, or null.
        //
        // UNTRUSTED INPUT. `returnTo` rides in the query string, which the
        // user (or anyone who hands them a link) can edit, and this component
        // turns it directly into navigation. So it is validated as a
        // SAME-ORIGIN ABSOLUTE PATH and nothing else:
        //
        //   * must start with a single '/', which rejects 'https://evil.host'
        //     and every other scheme-bearing string;
        //   * must NOT start with '//', which the browser reads as
        //     protocol-relative -- '//evil.host/x' looks like a path and is
        //     not one. This is the case a naive startsWith('/') check misses.
        //
        // Anything else is dropped silently rather than surfaced as an error:
        // a malformed return path is not something the user asked for or can
        // act on, and the fallback below is always a valid destination.
        safeReturnTo() {
            const raw = this.$route?.query?.returnTo
            if (typeof raw !== 'string') return null
            const path = raw.trim()
            if (!path.startsWith('/')) return null
            if (path.startsWith('//')) return null
            return path
        },

        // One id from either source (see the prop comment).
        effectiveArtworkId() {
            return this.artwork_id || this.artworkId || null
        },
        selected() {
            return (
                this.colorways.find((c) => c.name === this.selectedName) || null
            )
        },
        // TRUE only for a genuinely standalone upload -- colorways are
        // loaded, but neither the response nor the mounted route carries a
        // version to save against. Distinct from "no colorways yet" (the
        // save panel does not apply before a design exists at all) and from
        // the artwork route (whose read always carries the version; a
        // different file there mints a new version first).
        saveUnavailableReason() {
            if (this.artworkVersionId || !this.colorways.length) return ''
            return (
                'This design was not opened from an artwork, so there is ' +
                'no version to save a colorway against. Open it from the ' +
                'decoration library to save your changes.'
            )
        },
        emptyMessage() {
            if (this.unsupportedReason) return this.unsupportedReason
            if (this.effectiveArtworkId) {
                return (
                    'This artwork has no embroidery colorways. Choose a .emb ' +
                    'or .dst file to map its threads.'
                )
            }
            return (
                'No design loaded yet. Choose a .emb or .dst file to see its ' +
                'colorways.'
            )
        },
        // Exposed so the template can v-for the options rather than
        // hard-coding three near-identical buttons that could drift apart.
        SUBSTRATE_OPTIONS() {
            return SUBSTRATE_OPTIONS
        },

        // The most recently saved colorway. Still used to seed the
        // default-for control on load, but NO LONGER the only one shown --
        // the list renders all of them (Jake, 2026-08-29: multi-save).
        currentSavedColorway() {
            if (!this.savedColorways.length) return null
            return this.savedColorways[this.savedColorways.length - 1]
        },

        // The colorway the editor opens on: the FIRST ONE SAVED for this
        // version (Jake, 2026-10-21: "load the default colorway (first
        // saved)"). LOWEST id, never list position -- the list endpoint
        // sorts by NAME for display, so list[0] is whichever name sorts
        // first. id is immutable (a replace is a PUT onto the same row; see
        // store-editor defaultColorway.js for the verification), so a rename
        // cannot change which colorway opens. Rows without an id are not
        // saved colorways and are skipped.
        firstSavedColorway() {
            const saved = this.savedColorways.filter((c) => c && c.id != null)
            if (!saved.length) return null
            return saved.reduce((a, b) => (b.id < a.id ? b : a))
        },

        activeSavedColorway() {
            if (this.activeSavedId == null) return null
            return (
                this.savedColorways.find((c) => c.id === this.activeSavedId) ||
                null
            )
        },

        // ALWAYS "Save". The label was "Replace colorway" while the UX showed
        // exactly one saved colorway, which was honest then and would be a
        // lie now: the button adds to a collection, and calling it Replace
        // would tell the user their existing colorways are about to be
        // destroyed.
        saveButtonLabel() {
            if (this.saving) return 'Saving...'
            return 'Save colorway'
        },

        anyChosen() {
            return Object.keys(this.chosen).length > 0
        },
        // TRUE once `applySuggestions` has seeded at least one stop with a
        // real hex. Distinct from `anyChosen`: seeding is OUR guess, not a
        // user decision, but it is still real thread data -- a full
        // <recolor> payload Wilcom can render from. This is what makes the
        // initial "no preview until a thread is chosen" rule (Bug 2's
        // original blocker) obsolete: that rule predates suggestions being
        // auto-applied on load, and a named colorway with seeded threads is
        // no longer the "name only, no payload" case that renders a ghost.
        anySeeded() {
            return Object.values(this.edits).some((e) => e && e.hex)
        },

        // The rows the table renders: STOPS, not palette slots.
        //
        // A colorway's `threads` is a fixed-size palette that Wilcom pads with
        // a stock default set, so rendering it directly asks the distributor to
        // choose threads for colors the machine never sews -- 15 rows for a
        // 1-color design, silently, behind a plausible-looking table. `stops`
        // is the sewn sequence and each entry indexes into the palette.
        //
        // Each row flattens its palette thread up so the rest of the template
        // (hex, description, suggested, isUnchosen) keeps working unchanged.
        //
        // FALLS BACK to the palette when `stops` is absent. That is not
        // defensive padding: a design whose stop sequence we could not read
        // still has to be editable, and an older cached payload predates the
        // field. The fallback is the OLD behaviour, which is wrong-but-usable
        // rather than empty.
        stopRows() {
            if (!this.selected) return []
            const stops = this.selected.stops
            if (!Array.isArray(stops) || !stops.length) {
                return (this.selected.threads || []).map((thread, i) => ({
                    ...thread,
                    stop_index: i + 1,
                    num_stitches: null,
                }))
            }
            // `family` rides along on the palette thread -- classified
            // server-side so the SPA never holds a second, drifting copy of
            // the color-family rules.
            return stops.map((stop, i) => ({
                ...(stop.thread || {}),
                stop_index: stop.stop_index ?? i + 1,
                color_idx: stop.color_idx,
                num_stitches: stop.num_stitches,
            }))
        },
    },

    mounted() {
        // Load THIS artwork's own design. The editor is mounted at an artwork
        // URL, so requiring a hand-upload every time made it a standalone tool
        // that merely lived at an artwork route. artworks.original_file_url
        // already holds the stitch file -- only the wiring was missing.
        if (this.effectiveArtworkId) this.loadFromArtwork()
    },

    beforeUnmount() {
        // A pending debounce must not fire a vendor call after teardown.
        if (this._previewTimer) clearTimeout(this._previewTimer)
        // Pending per-stop resolves must not fire after teardown either.
        Object.values(this._resolveTimers).forEach((t) => clearTimeout(t))
    },

    methods: {
        async loadFromArtwork() {
            this.loading = true
            this.error = ''
            const epoch = this.designEpoch
            try {
                const { data } = await api.get(
                    `/wilcom/artwork/${this.effectiveArtworkId}/colorways`,
                    { params: { chart: this.chart } }
                )
                // A file chosen while this read was in flight is the newer
                // design; the artwork's answer must not replace it.
                if (epoch !== this.designEpoch) return
                // The artwork's own design is now the active one, so a File
                // left over from a previous upload must not hijack the render.
                this._designFile = null
                // An artwork that CANNOT have colorways answers 200 with a
                // reason (not a 400) -- opening the page is a valid request
                // with an empty answer, and a 400 logged a red console error
                // on every visit to a non-embroidery artwork.
                if (data && data.supported === false) {
                    this.unsupportedReason = data.reason || ''
                    this.colorways = []
                    return
                }
                this.unsupportedReason = ''
                // RE-ENTRY OPENS ON THE FIRST SAVED COLORWAY (Jake,
                // 2026-10-21). Only here, on the artwork's OWN design: an
                // uploaded file is a different design, and a colorway saved
                // for this one would put its threads on unrelated stops.
                // Not awaited -- the stops render from the suggestions at
                // once and the saved threads replace them when the list
                // lands (normally inside the preview debounce, so the one
                // render Wilcom does is of the saved colorway).
                this.applyColorways(data).then(() =>
                    this.applyFirstSavedColorway(epoch),
                )
            } catch (err) {
                // Same rule as the success path: a newer design owns the
                // page, so a stale read's failure is not its error.
                if (epoch !== this.designEpoch) return
                // A 400 means this artwork simply has no stitch file (e.g. a
                // PNG). That is NOT a user-facing error -- it just means the
                // upload affordance is the way in. Anything else IS an error.
                const status = err?.response?.status
                if (status === 400) {
                    // Not an error for the USER -- this artwork simply cannot
                    // have colorways. Surface the server's specific reason
                    // (e.g. "a .dst carries no colorway data") as guidance.
                    this.unsupportedReason = this._messageFor(err)
                } else if (status !== 404) {
                    this.error = this._messageFor(err)
                }
            } finally {
                // The newer design's own load owns the spinner.
                if (epoch === this.designEpoch) this.loading = false
            }
        },

        applyColorways(data) {
            this.colorways = data.colorways || []
            this.design = data.design || null
            this.sourceName = formatArtworkName(data.source && data.source.filename)
            this.fromArtwork = Boolean(data.source && data.source.from_artwork)
            // The version a saved colorway hangs off. Only the ARTWORK read
            // carries one; a standalone upload has none, so it cannot be
            // saved (saveUnavailableReason says why). An upload at an artwork
            // route used to BORROW the artwork's version, which saved one
            // file's threads against a different file's version -- the
            // reason a swapped file "wasn't saved" (Jake, 2026-10-21). A
            // different file there now mints its own version first
            // (confirmReplaceFile) and the editor re-reads the artwork.
            this.artworkVersionId =
                (data.source && data.source.artwork_version_id) || null
            const listed = this.artworkVersionId
                ? this.loadSavedColorways()
                : Promise.resolve()
            // The design's OWN current colorway, not index 0.
            const current = this.colorways.find((c) => c.current)
            this.selectedName =
                (current || this.colorways[0] || {}).name || null
            this.applySuggestions()
            // Resolves once the saved list is in (or has failed, which
            // loadSavedColorways already absorbs).
            return listed
        },

        // Put the first saved colorway on the stops, unless something newer
        // has happened since the read that asked for it.
        //
        // Skipped when the user already TOUCHED a stop: the list can land
        // after they started editing, and replacing their choice with a
        // saved one would silently discard work. Skipped when a different
        // design took the stage meanwhile (epoch), for the reason above.
        applyFirstSavedColorway(epoch) {
            if (epoch !== this.designEpoch || this._designFile) return
            const first = this.firstSavedColorway
            if (!first) return
            const touched = Object.values(this.edits).some(
                (e) => e && e.touched,
            )
            if (touched) return
            this.loadSavedColorway(first)
        },

        onFileChange(event) {
            const input = event.target
            const file = input && input.files && input.files[0]
            // Cleared so choosing the SAME file again (after a Cancel) fires
            // another change event.
            if (input) input.value = ''
            if (!file) return
            // At an artwork route a different file is a new VERSION of the
            // decoration (Jake, 2026-10-21): ask first, change nothing yet.
            if (this.effectiveArtworkId) {
                const reason = this._unusableDesignFileReason(file)
                if (reason) {
                    this.error = reason
                    return
                }
                this.error = ''
                this.replaceFileError = ''
                this.pendingReplaceFile = file
                // Focus lands in the dialog (on the safe choice), so Esc and
                // the keyboard act on it rather than on the page behind.
                this.$nextTick(() => {
                    const cancel = this.$refs.replaceFileCancel
                    if (cancel && cancel.focus) cancel.focus()
                })
                return
            }
            this.handleFile(file)
        },

        // Why a chosen file cannot be a design, or '' when it can. Emptiness
        // first, deliberately -- see handleFile.
        _unusableDesignFileReason(file) {
            if (isEmptyFile(file)) return ARTWORK_UPLOAD_ERROR_EMPTY
            if (!/\.(emb|dst)$/i.test(file.name)) {
                return 'Colorways need an embroidery file (.emb or .dst).'
            }
            return ''
        },

        // aria-modal promises the page behind is inert, so Tab must not walk
        // out of the dialog into it: wrap from the last control to the first
        // (and Shift+Tab the other way).
        trapReplaceDialogFocus(event) {
            const dialog = this.$refs.replaceFileDialog
            if (!dialog) return
            const focusable = Array.from(
                dialog.querySelectorAll(
                    'button:not([disabled]), [href], input:not([disabled]), ' +
                        '[tabindex]:not([tabindex="-1"])',
                ),
            )
            if (!focusable.length) {
                event.preventDefault()
                return
            }
            const first = focusable[0]
            const last = focusable[focusable.length - 1]
            const active = document.activeElement
            const inside = dialog.contains(active)
            if (event.shiftKey && (active === first || !inside)) {
                event.preventDefault()
                last.focus()
            } else if (!event.shiftKey && (active === last || !inside)) {
                event.preventDefault()
                first.focus()
            }
        },

        cancelReplaceFile() {
            if (this.replacingFile) return
            this.pendingReplaceFile = null
            this.replaceFileError = ''
        },

        // Upload the chosen file and mint it as the decoration's new CURRENT
        // version through the one version-minting path,
        // POST /artworks/<id>/versions: the server checks the name against
        // the bytes and gives the version its own preview. Then read the
        // artwork again, so the editor shows the new version. Its saved
        // colorways start empty -- they hang off the version, and the old
        // version keeps its own.
        async confirmReplaceFile() {
            const file = this.pendingReplaceFile
            if (!file || this.replacingFile) return
            this.replacingFile = true
            this.replaceFileError = ''
            try {
                const form = new FormData()
                form.append('file', file, file.name)
                const uploaded = await api.post('/upload_image', form)
                const key = uploaded && uploaded.data && uploaded.data.uuid
                if (!key) {
                    throw new Error('The file did not upload. Try again.')
                }
                await api.post(`/artworks/${this.effectiveArtworkId}/versions`, {
                    file_url: buildImageUrl(key),
                    original_filename: file.name,
                })
            } catch (err) {
                this.replaceFileError =
                    err && err.response ? this._messageFor(err) : err.message
                return
            } finally {
                this.replacingFile = false
            }
            this.pendingReplaceFile = null
            // Everything on the stage describes the previous version.
            this._resetForNewDesign()
            this.filename = ''
            this.colorways = []
            this.edits = {}
            this.chosen = {}
            this.previewUrl = ''
            this.savedColorways = []
            this.artworkVersionId = null
            this._designFile = null
            await this.loadFromArtwork()
        },

        async handleFile(file) {
            this.error = ''
            this.previewUrl = ''
            this.chosen = {}
            this.edits = {}
            this.colorways = []
            this.filename = file.name
            this._resetForNewDesign()

            // Emptiness is checked BEFORE the extension, deliberately.
            // A Google Drive / iCloud / OneDrive placeholder .dst is a
            // perfectly supported TYPE that simply has no bytes yet --
            // blaming the format for it is what sent Jake and a
            // teammate chasing a nonexistent file-format bug. Byte-level
            // reality outranks the filename. See
            // utils/artworkUploadContract.js for the full root cause.
            if (isEmptyFile(file)) {
                this.error = ARTWORK_UPLOAD_ERROR_EMPTY
                return
            }

            // .dst is supported too (verified live: Blair.dst returned 1
            // colorway / 56 threads and recolored correctly). Only reject
            // what genuinely has no stitches, e.g. a PNG.
            if (!/\.(emb|dst)$/i.test(file.name)) {
                this.error =
                    'Colorways need an embroidery file (.emb or .dst).'
                return
            }

            this._designFile = file
            this.loading = true
            const epoch = this.designEpoch
            try {
                const form = new FormData()
                form.append('file', file)
                form.append('chart', this.chart)
                const { data } = await api.post('/wilcom/design-colorways', form)
                // A second file chosen before this one parsed wins.
                if (epoch !== this.designEpoch) return
                this.applyColorways(data)
            } catch (err) {
                if (epoch !== this.designEpoch) return
                this.error = this._messageFor(err)
            } finally {
                if (epoch === this.designEpoch) this.loading = false
            }
        },

        // A DIFFERENT DESIGN IS TAKING THE STAGE: drop every piece of state
        // that describes the previous one (Jake, 2026-10-21: "when I choose a
        // different file ... the preview doesn't update").
        //
        // The bug this closes, reproduced on 8089: the highlight, its
        // pre-highlight snapshot and its per-(colorway, stop) render cache
        // all survived a file swap. They are keyed by colorway NAME, which
        // two files routinely share ("Colorway 1"), so after the swap the
        // old stop stayed lit and "un-highlighting" it restored a picture of
        // the OLD file under the new file's stops. The epoch bump also
        // retires any render or highlight still in flight for the old file.
        _resetForNewDesign() {
            this.designEpoch += 1
            if (this._previewTimer) {
                clearTimeout(this._previewTimer)
                this._previewTimer = null
            }
            this.previewBusy = false
            // A parse still in flight for the previous file will be dropped
            // and never reach its own `finally`, so its spinner goes here.
            this.loading = false
            this.highlightedStop = null
            this.highlightBusy = false
            this._preHighlightPreviewUrl = null
            this._highlightCache = {}
            this.pickerStop = null
            this.stopErrors = {}
            this.replacePrompt = null
            this.activeSavedId = null
        },

        _messageFor(err) {
            // The fallback must NOT assert a specific cause it does not
            // actually know. "Could not read colorways from that file" was
            // wrong for its most common trigger -- .dst genuinely carries
            // colorway data (verified live against Blair.dst and
            // odcircle.dst) -- so that string sent both Jake and a
            // teammate chasing a nonexistent file-format bug for an hour
            // when the real failure was something else entirely (still
            // undiagnosed). A generic, honest message is strictly more
            // useful than a confident wrong one.
            // Prefer whatever the SERVER actually said, in every shape
            // our endpoints emit. wilcom_preview.py returns
            // {error: {message}}; upload.py returns a bare {error: str}.
            // Reading only the nested shape is how "file is required"
            // got replaced by the generic line below -- the server had
            // named the cause and the UI threw it away. Matches the
            // treatment AddArtworkWizard.vue got in #2893.
            //
            // The axios message is deliberately NOT a fallback: on a
            // 400 it reads "timeout exceeded" / "Request failed with
            // status code 400", neither of which is a cause.
            const data = err?.response?.data
            const fromServer =
                (typeof data?.error === 'string' ? data.error : null) ||
                data?.error?.message ||
                data?.message ||
                (typeof data === 'string' ? data : null)
            if (fromServer) return fromServer
            return 'Something went wrong reading that file. Try again, or try a different file.'
        },

        selectColorway(name) {
            if (name === this.selectedName) return
            this.selectedName = name
            // Thread choices belong to a colorway; carrying them across would
            // silently apply one colorway's threads to another.
            this.chosen = {}
            this.edits = {}
            this.stopErrors = {}
            this.previewUrl = ''
            // Same reasoning for the highlight and the picker: stop 2 of the
            // previous colorway is meaningless here, and the new colorway may
            // not even have one.
            this.highlightedStop = null
            this.pickerStop = null
            // The cache and the pre-highlight snapshot are keyed to THIS
            // colorway's palette -- a different colorway recolors the same
            // stop differently, so carrying either across would show a
            // Colorway 1 render as if it were Colorway 2's.
            this._highlightCache = {}
            this._preHighlightPreviewUrl = null
            // The stops now carry suggestions for a different colorway, so
            // no saved colorway is on them any more.
            this.activeSavedId = null
            this.applySuggestions()
        },

        async loadSavedColorways() {
            if (!this.artworkVersionId) return
            try {
                const { data } = await api.get(
                    `/wilcom/artwork-versions/${this.artworkVersionId}/colorways`,
                )
                this.savedColorways = (data && data.colorways) || []
                // DELIBERATELY DOES NOT SEED substrateTarget from an
                // existing colorway. This used to, and was right to: while
                // the UX showed exactly ONE saved colorway the panel
                // effectively edited it, so showing its declaration is what
                // made "set it once" survive a reload.
                //
                // Multi-save changed what the panel IS -- it authors an
                // ADDITIONAL colorway. Seeding from the newest would
                // silently pre-restrict the next save, so a user with a
                // "Darks" colorway would find their next one quietly marked
                // dark-only having chosen nothing. Loading a saved colorway
                // still adopts its default-for (see loadSavedColorway); that
                // is an explicit "continue from this one", not an inherited
                // default.
            } catch (err) {
                // A failed LIST must not block editing -- the user can still
                // map threads and save. Surfacing it as the save error would
                // be a lie about an action they did not take.
                // eslint-disable-next-line no-console
                console.warn('colorway-editor: could not list saved colorways', describeError(err))
            }
        },

        // The threads to persist, keyed by STOP -- the sewn sequence, which is
        // what a colorway actually is. Reading `chosen`/`edits` by stop index
        // keeps this aligned with the table the user just filled in.
        savedThreadsPayload() {
            return this.stopRows.map((stop, idx) => {
                const state = this.stopState(idx)
                return {
                    stop_index: stop.stop_index ?? idx + 1,
                    code: state.madeira || null,
                    hex: state.hex || null,
                    brand: state.brand || null,
                }
            })
        },

        // The ORDER the pill cycles through, matching SUBSTRATE_OPTIONS so
        // the two cannot drift: all -> light -> dark -> all.
        nextSubstrate(value) {
            const order = SUBSTRATE_OPTIONS.map((o) => o.value)
            const i = order.findIndex((v) => (v ?? null) === (value ?? null))
            return order[(i + 1) % order.length]
        },

        // Per-role modifier class. The three roles must be tellable APART at
        // a glance (Jake: make it obvious), so each gets its own class and
        // the stylesheet gives it its own colour -- rather than one class
        // whose text is the only difference.
        substrateClass(value) {
            const opt = SUBSTRATE_OPTIONS.find(
                (o) => (o.value ?? null) === (value ?? null),
            )
            return `cwe__saved-target--${opt ? opt.key || opt.value : 'all'}`
        },

        // Reclassify ONE saved colorway. Writes that row and only that row.
        //
        // NOT AN EXCLUSIVE DEFAULT (Jake, 2026-09-01): several colorways may
        // share a substrate -- a monochrome dark one and a full-colour dark
        // one is his own example. So there is deliberately no "clear this
        // role from the others" step here; adding one would be the bug.
        //
        // Sends ONLY substrate_target. Omitting `threads` is load-bearing:
        // the server's set_threads revokes a human confirmation when the
        // threads change, and re-sending an identical array to say something
        // about the SUBSTRATE must not risk revoking one.
        async cycleSubstrate(colorway) {
            if (this.reclassifyingId) return
            const next = this.nextSubstrate(colorway.substrate_target)
            this.reclassifyingId = colorway.id
            this.saveError = ''
            try {
                const { data } = await api.put(
                    `/wilcom/artwork-versions/${this.artworkVersionId}` +
                        `/colorways/${colorway.id}`,
                    { substrate_target: next },
                )
                // Trust the SERVER's echo, not the optimistic guess -- it is
                // the authority on what was stored.
                this.savedColorways = this.savedColorways.map((c) =>
                    c.id === colorway.id
                        ? { ...c, substrate_target: data
                            ? data.substrate_target
                            : next }
                        : c,
                )
            } catch (err) {
                this.saveError =
                    err?.response?.data?.error?.message ||
                    'Could not change what this colorway applies to.'
            } finally {
                this.reclassifyingId = null
            }
        },

        // A plain SELECT, not a toggle. The old toggle existed so that
        // "undeclared" (null) stayed reachable after a mis-click, since it
        // had no button of its own. "All colors" IS a button now, so
        // clicking the selected side to clear it would be a second, hidden
        // way to reach a state that already has a visible one -- and a
        // control where clicking the lit option un-lights it is a surprise.
        selectSubstrate(value) {
            this.substrateTarget = value ?? null
        },

        // Stable per-option testid/key. 'all' is `value: null`, which cannot
        // be interpolated into a selector, so options carry an explicit
        // `key` and fall back to their value.
        substrateKey(opt) {
            return opt.key || opt.value
        },

        // How a saved colorway's default-for reads in the list. NULL renders
        // as "All colors", NEVER as blank: blank says "nobody answered", when
        // in fact null IS the answer (Jake, 2026-08-29).
        substrateLabel(value) {
            const opt = SUBSTRATE_OPTIONS.find(
                (o) => (o.value ?? null) === (value ?? null),
            )
            return opt ? opt.label : String(value)
        },

        // Delete a saved colorway. THE ONLY PLACE COLORWAYS GET DELETED.
        //
        // NOT OPTIMISTIC. The row is removed only after the server confirms,
        // because the server legitimately REFUSES: a colorway that variants
        // are bound to returns 409 (Jake, 2026-08-29) rather than cascading,
        // since clearing a variant's frozen thread snapshot would change what
        // an already-approved placement sews. Removing the row first and
        // restoring it on failure would flash the delete as if it had worked.
        async deleteColorway(colorway) {
            if (this.deletingId) return
            this.deletingId = colorway.id
            this.saveError = ''
            try {
                await api.delete(
                    `/wilcom/artwork-versions/${this.artworkVersionId}` +
                        `/colorways/${colorway.id}`,
                )
                this.savedColorways = this.savedColorways.filter(
                    (c) => c.id !== colorway.id,
                )
                if (this.activeSavedId === colorway.id) this.activeSavedId = null
            } catch (err) {
                // SURFACE the server's own message -- it names the products
                // still using this colorway, which is the whole point of the
                // 409. A generic "could not delete" would leave the user
                // clicking a button that never works with no idea why.
                this.saveError =
                    err?.response?.data?.error?.message ||
                    'Could not delete this colorway.'
            } finally {
                this.deletingId = null
            }
        },

        async saveColorway() {
            const name = (this.saveName || '').trim()
            // The server is the authority (400 on empty, 409 on duplicate);
            // this only spares a pointless round trip.
            if (!name) {
                this.saveError = 'Give the colorway a name before saving.'
                return
            }
            this.saving = true
            this.saveError = ''
            // Captured ONCE, before the request. If this save turns out to
            // collide, the replace dialog re-sends exactly these threads --
            // re-reading the table at confirm time would silently save
            // whatever the user edited while the question was on screen.
            const threads = this.savedThreadsPayload()
            try {
                const { data } = await api.post(
                    `/wilcom/artwork-versions/${this.artworkVersionId}/colorways`,
                    {
                        name,
                        threads,
                        // Explicit null, never omitted. The server treats an
                        // absent key as "leave unchanged" and null as
                        // "undeclared"; sending null says what the user
                        // actually chose, which is nothing.
                        substrate_target: this.substrateTarget,
                    },
                    // THIS SAVE ANSWERS ITS OWN ERRORS. The global axios
                    // interceptor toasts every non-401/404 status, so a
                    // duplicate-name 409 put an app-level error snackbar
                    // beside the replace dialog (Jake, 2026-10-27: "the error
                    // snackbar ... shouldn't be there. There's already UI
                    // asking if you want to replace"). The catch below shows
                    // every failure of this request itself: the dialog for
                    // duplicate_name, `saved-colorway-error` for the rest, so
                    // a genuine error is still visible, once, in place.
                    { [SUPPRESS_ERROR_TOAST]: true },
                )
                if (data && data.id) this.savedColorways = [...this.savedColorways, data]
                this.saveName = ''
            } catch (err) {
                const detail = err?.response?.data?.error
                // A NAME COLLISION IS A QUESTION, NOT A FAILURE (Jake,
                // 2026-09-01). The server refuses to overwrite silently --
                // rightly, since a teammate's colorway must not vanish
                // because someone reused a name -- but the answer to "that
                // name is taken" is to ASK, not to lose the user's work.
                //
                // Keyed on the error CODE, never on the message text: a
                // wording change must not turn the dialog back into a dead
                // end. Any other failure still surfaces as an error, because
                // "replace?" is not a meaningful answer to a 500.
                //
                // The server sends the colliding row's id, which is what
                // makes the replace a PUT to a known row rather than a
                // client-side re-match of the same case rule.
                if (detail?.code === 'duplicate_name' && detail?.colorway_id) {
                    // THE DIALOG IS THE WHOLE ANSWER (Jake, 2026-09-02:
                    // "I see both a replacement confirmation dialog, and an
                    // error snackbar. The error bar shouldn't be shown for
                    // that usecase.")
                    //
                    // The banner beside it is NOT this save's -- it is the
                    // component's OTHER error surface, written by the
                    // debounced preview render (600ms) whose timer routinely
                    // lands mid-save. Nothing in the save path cleared it,
                    // so a stale render failure sat next to a question about
                    // a name.
                    //
                    // Cleared ONLY here. A failed render still deserves a
                    // banner when the save succeeds or fails for any other
                    // reason -- what makes this case different is that the
                    // dialog already asks the user for exactly one decision,
                    // and a red banner beside it reads as "your answer will
                    // not work".
                    this.error = ''
                    this.replacePrompt = {
                        colorwayId: detail.colorway_id,
                        // What the user typed -- the casing a replace adopts.
                        name,
                        // What is already stored, so the question can name
                        // both and the difference is visible.
                        existingName: detail.existing_name || name,
                        threads,
                        substrateTarget: this.substrateTarget,
                    }
                } else {
                    // SURFACE the failure. Swallowing it would leave the
                    // user believing they had saved and finding the OLD
                    // threads later.
                    this.saveError =
                        detail?.message || 'Could not save this colorway.'
                }
            } finally {
                this.saving = false
            }
        },

        // Carry out the replace the user just approved.
        //
        // A PUT ONTO THE COLLIDING ROW, not delete-then-create. The id
        // survives, so anything already bound to this colorway
        // (variants.colorway_id) stays bound -- a delete/create pair would
        // silently break those bindings to achieve the same visible result.
        //
        // The NEW casing is sent as the name: saving 'Navy' over 'navy'
        // leaves the row named 'Navy'. Jake's ruling -- what he typed is
        // what he gets.
        async confirmReplace() {
            const pending = this.replacePrompt
            if (!pending || this.saving) return
            this.saving = true
            this.saveError = ''
            try {
                const { data } = await api.put(
                    `/wilcom/artwork-versions/${this.artworkVersionId}` +
                        `/colorways/${pending.colorwayId}`,
                    {
                        name: pending.name,
                        threads: pending.threads,
                        substrate_target: pending.substrateTarget,
                    },
                )
                // Replace the row IN PLACE. Appending would show the
                // colorway twice -- the exact duplicate-looking list this
                // whole change exists to end.
                const updated = data || {
                    id: pending.colorwayId,
                    name: pending.name,
                    threads: pending.threads,
                    substrate_target: pending.substrateTarget,
                }
                const known = this.savedColorways.some(
                    (c) => c.id === pending.colorwayId,
                )
                this.savedColorways = known
                    ? this.savedColorways.map((c) =>
                          c.id === pending.colorwayId ? { ...c, ...updated } : c,
                      )
                    : [...this.savedColorways, updated]
                this.replacePrompt = null
                this.saveName = ''
            } catch (err) {
                this.saveError =
                    err?.response?.data?.error?.message ||
                    'Could not replace this colorway.'
                // The dialog CLOSES on failure. Leaving it open next to an
                // error about the same action invites a retry loop against
                // whatever just refused.
                this.replacePrompt = null
            } finally {
                this.saving = false
            }
        },

        // Abandon the replace. Writes NOTHING -- and deliberately KEEPS the
        // typed name in the field, because "that name is taken" is usually
        // answered by editing the name, not by giving up on the save.
        cancelReplace() {
            this.replacePrompt = null
        },

        // Apply a saved colorway's threads back onto the stops. Matched by
        // STOP INDEX rather than array position: a saved colorway and the
        // design it is loaded onto can legitimately differ in length, and
        // position-matching would shift every thread by one.
        loadSavedColorway(colorway) {
            // Load means "continue from this one", so its default-for comes
            // along -- otherwise loading a dark-only colorway and re-saving
            // would silently DISCARD the restriction. `?? null` and not
            // `||`: the API sends null for "all", and the two must not be
            // conflated with a future falsy value.
            this.substrateTarget = colorway.substrate_target ?? null
            const byStop = new Map(
                usableSavedThreads(colorway).map((t) => [t.stop_index, t]),
            )
            this.stopRows.forEach((stop, idx) => {
                const saved = byStop.get(stop.stop_index ?? idx + 1)
                if (!saved || !saved.code) return
                this.chooseThread(idx, {
                    code: saved.code,
                    hex: saved.hex,
                    brand: saved.brand,
                    // A saved colorway is a past DECISION, not a guess.
                    exact: true,
                })
            })
            this.activeSavedId = colorway.id
        },

        // $router.push rather than a <router-link>: it is the idiom the sibling
        // detail pages already use (DecorationLibraryArtworkDetail.vue:1369),
        // and router-link is not registered in the component test environment,
        // so a link would be untestable here without stubbing the router.
        //
        // ONE way out, whose destination depends on where the user came from
        // (card https://trello.com/c/vc8U5340). The product editor's routing
        // card sends people here to author a colorway; returning them to the
        // decoration library would cost them the store, product and placement
        // they were configuring. A SECOND back button for that case would be a
        // second mechanism for "leave this editor" (principle 2) -- arriving
        // from elsewhere changes where back goes, not what back means.
        backToArtwork() {
            const back = this.safeReturnTo
            if (back) {
                this.$router.push(back)
                return
            }
            this.$router.push({
                name: 'library_artwork_detail',
                params: { artwork_id: String(this.effectiveArtworkId) },
            })
        },

        openPicker(idx) {
            this.pickerStop = idx
        },

        // A thread chosen from the catalogue is a DECISION, not a guess, so it
        // is marked exact. `chooseThread` derives its "closest match" flag from
        // `exact`, and a picked thread carries no such field -- left alone it
        // would default to approximate and flag every deliberate choice. That
        // would train people to ignore the flag on the stops where it actually
        // means something.
        onPickThread(thread) {
            if (this.pickerStop === null) return
            this.chooseThread(this.pickerStop, { ...thread, exact: true })
            this.pickerStop = null
        },

        // Isolate one stop in the preview, or clear it. Toggling on the same
        // stop is the way OUT -- a highlight with no exit strands the user
        // looking at a single stop with no obvious way back to the whole design.
        //
        // THE BUG THIS FIXES: `highlightedStop` used to be consumed ONLY by
        // CSS (the row tint + button state below). The preview is a flat PNG
        // from Wilcom -- CSS cannot dim a region inside a raster -- so the
        // toggle changed on screen but the image never did. This now asks
        // the SERVER for a render with that one stop isolated, same as the
        // recolor preview asks for the user's chosen threads.
        async toggleHighlight(idx) {
            if (this.highlightedStop === idx) {
                // The way OUT. We already have the un-highlighted image (it
                // is exactly what was showing before this highlight started)
                // -- restoring it costs nothing.
                this.highlightedStop = null
                this.previewUrl = this._preHighlightPreviewUrl || ''
                return
            }

            const stop = this.stopRows[idx]
            if (!stop || !this.selected) return
            const stopIndex = stop.stop_index ?? idx + 1

            // Capture the CURRENT preview as what to restore to -- but only
            // the FIRST time, so switching directly between two highlighted
            // stops (0 -> 1 -> 0) does not overwrite the real baseline with
            // an already-highlighted image.
            if (this.highlightedStop === null) {
                this._preHighlightPreviewUrl = this.previewUrl
            }
            this.highlightedStop = idx

            const cacheKey = `${this.selectedName}::${stopIndex}`
            const cached = this._highlightCache[cacheKey]
            if (cached) {
                this.previewUrl = cached
                return
            }

            this.highlightBusy = true
            const epoch = this.designEpoch
            try {
                // Built from `stopRows` (not `this.selected.stops` directly)
                // so the FALLBACK path -- a design whose stop sequence could
                // not be read, where stopRows synthesizes one row per palette
                // slot -- still resolves a color_idx. In that fallback the
                // row's position i.e. IS the palette slot, which is exactly
                // what stopRows already encodes for the palette-thread path.
                const stops = this.stopRows.map((row, i) => ({
                    stop_index: row.stop_index ?? i + 1,
                    color_idx: row.color_idx ?? i,
                }))
                const paletteSize = (this.selected.threads || []).length
                const body = {
                    colorway: this.selectedName,
                    stops,
                    palette_size: paletteSize,
                    stop_index: stopIndex,
                }

                let data
                if (this._designFile) {
                    const form = new FormData()
                    form.append('file', this._designFile)
                    form.append('colorway', body.colorway)
                    form.append('stops', JSON.stringify(stops))
                    form.append('palette_size', String(paletteSize))
                    form.append('stop_index', String(stopIndex))
                    ;({ data } = await api.post(
                        '/wilcom/design-colorway-highlight',
                        form
                    ))
                } else if (this.effectiveArtworkId) {
                    ;({ data } = await api.post(
                        `/wilcom/artwork/${this.effectiveArtworkId}/colorway-highlight`,
                        body
                    ))
                } else {
                    // Nothing to render against (no artwork, no upload) --
                    // leave the toggle state as visual-only, same as before
                    // this fix, rather than throwing.
                    return
                }
                // A render of a design that has since been replaced must not
                // be cached or shown as the new design's highlight.
                if (epoch !== this.designEpoch) return
                this._highlightCache = { ...this._highlightCache, [cacheKey]: data.image_url }
                this.previewUrl = data.image_url || this.previewUrl
            } catch (err) {
                if (epoch !== this.designEpoch) return
                // HONEST FAILURE: surface it, and leave the PREVIOUS preview
                // on screen. Never blank it or show a stale/wrong image as if
                // it were the highlight -- that would be a silently wrong
                // picture, worse than an error banner. The button state
                // reverts too, so it does not claim a stop is isolated when
                // the image on screen is still the whole design.
                this.error = this._messageFor(err)
                this.highlightedStop = null
            } finally {
                if (epoch === this.designEpoch) this.highlightBusy = false
            }
        },

        // Thousands separators: stitch counts run to five and six figures, and
        // "104330" is materially harder to read than "104,330" in a table a
        // decorator scans for cost. Null (no stop sequence) renders as a dash
        // rather than "0", which would be a false statement about the design.
        formatStitches(n) {
            if (n === null || n === undefined || Number.isNaN(Number(n))) {
                return '--'
            }
            return Number(n).toLocaleString('en-US')
        },

        applySuggestions() {
            // Seed every stop from its nearest-thread suggestion so the table
            // is usable immediately -- a 15-stop design otherwise needs 15
            // clicks before anything can be previewed, and showing a
            // suggestion the user has NOT chosen makes displayed state
            // disagree with real state.
            //
            // This is a STARTING POINT, not a decision. `touched` stays FALSE,
            // so the approximate and "pick a thread" markers still render:
            // a distance-233 guess must not look settled just because we
            // pre-filled it. The user overrides by typing, exactly as before.
            const cw = this.selected
            if (!cw) return
            const seeded = {}
            ;(cw.threads || []).forEach((stop, idx) => {
                const s = stop.suggested
                if (!s) return
                seeded[idx] = {
                    hex: s.hex,
                    madeira: s.code,
                    brand: s.brand,
                    approx: !s.exact,
                    unknownCode: false,
                    // NOT touched: this is our guess, not the user's choice.
                    touched: false,
                }
            })
            this.edits = seeded
            // Render as soon as real thread data exists, without waiting for
            // the user to touch anything (Bug 2: "I shouldn't have to
            // change something to trigger that"; Bug 3: this also fires on
            // every colorway switch via selectColorway, so the preview
            // follows the selection instead of going stale). Debounced same
            // as every other preview trigger.
            if (Object.keys(seeded).length) this.schedulePreview()
        },

        isUnchosen(stop) {
            return !stop.brand || stop.brand === 'Default'
        },

        needsChoice(stop) {
            const d = stop?.suggested?.distance
            return typeof d === 'number' && d > NEEDS_CHOICE_DISTANCE
        },

        // The way BACK to the suggestion (Jake, 2026-08-20: "There's no way
        // to go back to closest match thread color"). Compares the CURRENT
        // Madeira code against the suggestion's -- not `touched`, which only
        // says the row was edited, not that it now differs. A row edited
        // back to the exact suggested code (or one loaded from a saved
        // colorway that happens to match) has nothing to revert.
        canRevert(idx) {
            const stop = this.stopRows[idx]
            const suggested = stop && stop.suggested
            if (!suggested || !suggested.code) return false
            return this.stopState(idx).madeira !== suggested.code
        },

        // Restore stop.suggested AND its guessed-pill state -- reverting
        // makes the row a guess again, because it is. Mirrors the seeding
        // shape in applySuggestions exactly, so a reverted row is
        // indistinguishable from one that was never touched.
        revertStop(idx) {
            const stop = this.stopRows[idx]
            const suggested = stop && stop.suggested
            if (!suggested) return
            this.edits = {
                ...this.edits,
                [idx]: {
                    hex: suggested.hex,
                    madeira: suggested.code,
                    brand: suggested.brand,
                    approx: !suggested.exact,
                    unknownCode: false,
                    // NOT touched: back to being our guess, not a decision.
                    touched: false,
                },
            }
            // A reverted row is no longer a deliberate pick, so it must not
            // keep carrying the "exact" chosen-thread record either.
            if (this.chosen[idx]) {
                const next = { ...this.chosen }
                delete next[idx]
                this.chosen = next
            }
            this.schedulePreview()
        },

        // ------------------------------------------------------------------
        // Live-linked editing: type a hex OR a Madeira code, the other is
        // derived. Equivalent to the mechanism in Mike's tf-tools prototype
        // (hex <-> Madeira <-> PMS); PMS is omitted here because the Pantone
        // crosswalk is held pending a licensing decision.
        // ------------------------------------------------------------------

        stopState(idx) {
            return (
                this.edits[idx] || {
                    hex: '',
                    madeira: '',
                    approx: false,
                    unknownCode: false,
                    touched: false,
                }
            )
        },

        stopError(idx) {
            return this.stopErrors[idx] || ''
        },

        _setStopError(idx, message) {
            const next = { ...this.stopErrors }
            if (message) next[idx] = message
            else delete next[idx]
            this.stopErrors = next
        },

        blurStop(idx) {
            // Validate on BLUR. Mid-typing, "#0" is invalid only because the
            // user has not finished -- complaining then is noise, and the
            // complaint outliving the fix is worse.
            const st = this.stopState(idx)
            const hex = (st.hex || '').trim()
            if (hex && !/^#[0-9a-fA-F]{6}$/.test(hex)) {
                this._setStopError(idx, `${hex} is not a #RRGGBB color`)
            } else {
                this._setStopError(idx, '')
            }
        },

        editStop(idx, field, value) {
            const prev = this.stopState(idx)
            // Optimistic: the field the user typed shows immediately, the
            // linked one fills in when the resolve returns.
            this.edits = {
                ...this.edits,
                [idx]: { ...prev, [field]: value, touched: true },
            }
            // Typing is progress -- drop any stale complaint immediately
            // rather than leaving it up while the user fixes the value.
            this._setStopError(idx, '')
            // Only ask the server about a COMPLETE hex. Resolving "#0" would
            // spend a call to be told what we already know, and its 400 was
            // what produced the stuck page-level banner.
            if (field === 'hex' && !/^#[0-9a-fA-F]{6}$/.test(String(value).trim())) {
                return
            }
            this.scheduleResolve(idx, field, value)
        },

        scheduleResolve(idx, field, value) {
            if (this._resolveTimers[idx]) clearTimeout(this._resolveTimers[idx])
            this._resolveTimers[idx] = setTimeout(() => {
                delete this._resolveTimers[idx]
                this.resolveStop(idx, field, value)
            }, RESOLVE_DEBOUNCE_MS)
        },

        async resolveStop(idx, field, value) {
            // Sequence guard: a slow response for an older keystroke must not
            // overwrite a newer one (the prototype hit this too).
            const seq = (this._resolveSeq[idx] || 0) + 1
            this._resolveSeq[idx] = seq
            try {
                const { data } = await api.post('/wilcom/colors/resolve', {
                        winner: field,
                        hex: field === 'hex' ? value : this.stopState(idx).hex,
                        madeira:
                            field === 'madeira'
                                ? value
                                : this.stopState(idx).madeira,
                        chart: this.chart,
                    }
                )
                if (this._resolveSeq[idx] !== seq) return
                const prev = this.stopState(idx)
                this.edits = {
                    ...this.edits,
                    [idx]: {
                        ...prev,
                        hex: data.hex || (field === 'hex' ? value : ''),
                        madeira:
                            data.madeira ||
                            (field === 'madeira' ? value : ''),
                        brand: data.brand || prev.brand,
                        approx: Boolean(data.approx && data.approx.madeira),
                        unknownCode: Boolean(data.unknown_code),
                        touched: true,
                    },
                }
                this.schedulePreview()
            } catch (err) {
                if (this._resolveSeq[idx] === seq) {
                    // Scope it to the row that caused it.
                    this._setStopError(idx, this._messageFor(err))
                }
            }
        },

        chooseThread(idx, thread) {
            this.chosen = { ...this.chosen, [idx]: thread }
            // Accepting a suggestion fills the editable fields too, so the
            // table always shows one truth rather than two competing ones.
            this.edits = {
                ...this.edits,
                [idx]: {
                    ...this.stopState(idx),
                    hex: thread.hex,
                    madeira: thread.code,
                    brand: thread.brand,
                    approx: !thread.exact,
                    unknownCode: false,
                    touched: true,
                },
            }
            this.schedulePreview()
        },

        schedulePreview() {
            if (this._previewTimer) clearTimeout(this._previewTimer)
            this._previewTimer = setTimeout(() => {
                this._previewTimer = null
                this.renderPreview()
            }, PREVIEW_DEBOUNCE_MS)
        },

        async renderPreview() {
            // Naming a colorway without threads renders a pale GHOST from
            // Wilcom (measured live), so never ask for one until real thread
            // data exists -- either the user chose one (anyChosen) or
            // applySuggestions seeded one (anySeeded). Seeding happens on
            // every load and every colorway switch, which is what makes the
            // initial and switch-triggered renders (Bugs 2 and 3) safe: a
            // full <recolor> payload rides along either way, never a bare
            // colorway name.
            if ((!this.anyChosen && !this.anySeeded) || !this.selected) return
            // RENDER THE DESIGN THAT IS ACTUALLY LOADED. Two sources, two
            // routes -- mirroring the read side:
            //   uploaded file   -> POST the bytes (design-colorway-render)
            //   artwork's file  -> server reads it (artwork/<id>/colorway-render)
            // Always using the artwork route was a real bug: an uploaded
            // design could be listed and edited but never previewed, and on a
            // PNG artwork the render 400'd (Jake's stack trace, artwork 56).
            const useUpload = Boolean(this._designFile)
            if (!useUpload && !this.effectiveArtworkId) return
            // Only the NEWEST render of the CURRENT design may write the
            // preview. Without this, a slow render of the previous file
            // (or an older thread choice) landing late overwrites the newer
            // picture -- and nothing on screen says it is stale.
            const epoch = this.designEpoch
            const seq = ++this.previewSeq
            const isCurrent = () =>
                epoch === this.designEpoch && seq === this.previewSeq
            this.previewBusy = true
            try {
                let data
                if (useUpload) {
                    const form = new FormData()
                    form.append('file', this._designFile)
                    form.append('colorway', this.selectedName)
                    form.append('threads', JSON.stringify(this.renderThreads()))
                    ;({ data } = await api.post(
                        '/wilcom/design-colorway-render',
                        form
                    ))
                } else {
                    ;({ data } = await api.post(
                        `/wilcom/artwork/${this.effectiveArtworkId}/colorway-render`,
                        {
                            colorway: this.selectedName,
                            threads: this.renderThreads(),
                        }
                    ))
                }
                if (!isCurrent()) return
                this.previewUrl = data.image_url || ''
            } catch (err) {
                if (!isCurrent()) return
                this.error = this._messageFor(err)
            } finally {
                if (isCurrent()) this.previewBusy = false
            }
        },

        renderThreads() {
            // Every stop, in order: the user's choice where they made one,
            // the design's own color otherwise. Wilcom recolors the WHOLE
            // colorway, so a partial list would silently drop the untouched
            // stops rather than leaving them alone.
            return (this.selected.threads || []).map((stop, idx) => {
                const edit = this.stopState(idx)
                const chosen = this.chosen[idx]
                return {
                    hex: edit.hex || (chosen && chosen.hex) || stop.hex,
                    code: edit.madeira || (chosen && chosen.code) || stop.code,
                    brand: edit.brand || (chosen && chosen.brand) || stop.brand,
                    description: stop.description || '',
                }
            })
        },
    },
}
</script>

<style scoped>
/* The replace-file confirmation (a different file is a new version). */
.cwe__modal-scrim {
    position: fixed;
    inset: 0;
    z-index: 1200;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(19, 55, 82, 0.45);
}
.cwe__modal {
    width: min(480px, calc(100vw - 32px));
    padding: var(--tf-sp-5, 24px);
    border-radius: var(--tf-radius-md, 12px);
    background: var(--tf-bg, #fff);
    box-shadow: 0 12px 40px rgba(19, 55, 82, 0.25);
    color: var(--tf-fg-2, #37474f);
}
.cwe__modal-title {
    margin: 0 0 var(--tf-sp-3, 12px);
    font-size: 18px;
    color: var(--tf-fg-1, #133752);
}
.cwe__modal-lead {
    margin: 0 0 var(--tf-sp-2, 8px);
    overflow-wrap: anywhere;
}
.cwe__modal-list {
    margin: 0 0 var(--tf-sp-3, 12px);
    padding-left: 20px;
}
.cwe__modal-list li + li {
    margin-top: 4px;
}
.cwe__modal-warn {
    margin: 0 0 var(--tf-sp-3, 12px);
    font-weight: 600;
    color: var(--tf-error-dark, #b3261e);
}
.cwe__modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--tf-sp-2, 8px);
}
.cwe {
    border: 1px solid var(--tf-border, #cfd8e3);
    border-radius: var(--tf-radius-md, 12px);
    padding: var(--tf-sp-4, 16px);
    background: var(--tf-bg, #fff);
}
.cwe__title-row {
    display: flex;
    align-items: center;
}
.cwe__title {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--tf-fg-1, #133752);
    margin: 0;
}
.cwe__sub,
.cwe__muted {
    color: var(--tf-fg-3, #546e7a);
    font-size: 0.8125rem;
}
.cwe__sub {
    margin: 4px 0 12px;
}
.cwe__stop-instruction {
    margin: 0 0 8px;
}
.cwe__upload {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}
.cwe__upload-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    font-size: 0.875rem;
    color: var(--tf-fg-2, #37474f);
}
.cwe__filename {
    font-size: 0.8125rem;
    color: var(--tf-fg-3, #546e7a);
}
.cwe__error {
    margin-top: 10px;
    color: var(--tf-error-dark, #b3261e);
    background: var(--tf-error-soft, #fdecea);
    border: 1px solid var(--tf-error, #d93025);
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 0.8125rem;
}
.cwe__loading,
.cwe__empty {
    margin-top: 12px;
    color: var(--tf-fg-3, #546e7a);
    font-size: 0.875rem;
}
.cwe__list {
    list-style: none;
    padding: 0;
    margin: 14px 0;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.cwe__list-item {
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1px solid var(--tf-border, #cfd8e3);
    border-radius: 10px;
    padding: 8px 10px;
    cursor: pointer;
}
.cwe__list-item--on {
    border-color: var(--tf-primary, #119ae9);
    box-shadow: 0 0 0 2px rgba(17, 154, 233, 0.18);
}
.cwe__swatches {
    display: inline-flex;
}
.cwe__swatch {
    width: 10px;
    height: 16px;
    border: 1px solid rgba(0, 0, 0, 0.12);
}
.cwe__list-name {
    font-size: 0.875rem;
    color: var(--tf-fg-1, #133752);
}
.cwe__badge {
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--tf-fg-3, #546e7a);
    border: 1px solid var(--tf-border, #cfd8e3);
    border-radius: 999px;
    padding: 1px 6px;
}
.cwe__stops {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
}
.cwe__stops th {
    text-align: left;
    font-weight: 600;
    color: var(--tf-fg-3, #546e7a);
    border-bottom: 1px solid var(--tf-border, #cfd8e3);
    padding: 6px 8px;
    white-space: nowrap;
}
.cwe__stops td {
    border-bottom: 1px solid var(--tf-surface-light, #f5f8fa);
    padding: 8px;
    vertical-align: middle;
}
.cwe__stop-n {
    color: var(--tf-fg-4, #78909c);
    width: 2.5rem;
}
.cwe__chip {
    display: inline-block;
    width: 14px;
    height: 14px;
    border-radius: 3px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    margin-right: 6px;
    vertical-align: -2px;
}
.cwe__unauthored {
    margin-left: 6px;
}
.cwe__family {
    margin-left: 6px;
}
/* Stitch counts are compared DOWN the column ("which stop is the expensive
   one"), so they are right-aligned and tabular -- proportional digits make
   5-figure numbers of different widths impossible to scan against each
   other. */
.cwe__th-stitches,
.cwe__stitches {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
}
.cwe__stitches {
    color: var(--tf-fg-4, #78909c);
}
.cwe__highlight {
    /* A visible button at rest (Jake, 2026-10-21: "the stop numbers are
       clickable, but that's not obvious"): bordered, filled, with a small
       drop shadow, so it reads as pressable before any hover. */
    border: 1px solid var(--tf-border, #cfd8e3);
    background: var(--tf-surface-light, #f5f8fa);
    border-radius: 6px;
    min-width: 32px;
    padding: 3px 9px;
    cursor: pointer;
    color: var(--tf-fg-1, #133752);
    font: inherit;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    box-shadow: 0 1px 1px rgba(19, 55, 82, 0.12);
    transition: background 0.12s, border-color 0.12s, box-shadow 0.12s;
}
.cwe__highlight:hover {
    border-color: var(--tf-accent, #1565c0);
    background: var(--tf-accent-bg, rgba(21, 101, 192, 0.07));
    color: var(--tf-accent, #1565c0);
}
.cwe__highlight:active {
    box-shadow: none;
    transform: translateY(1px);
}
.cwe__highlight:focus-visible {
    outline: 2px solid var(--tf-accent, #1565c0);
    outline-offset: 2px;
}
/* The isolated stop is marked on BOTH the button and its row: the button
   alone is easy to lose in a 56-row table once you have scrolled away from
   it. */
.cwe__highlight--on,
.cwe__highlight--on:hover {
    background: var(--tf-accent, #1565c0);
    border-color: var(--tf-accent, #1565c0);
    color: #fff;
}
.cwe__stop-row--hot {
    background: var(--tf-accent-bg, rgba(21, 101, 192, 0.07));
}
.cwe__input {
    border: 1px solid var(--tf-border, #cfd8e3);
    border-radius: 6px;
    padding: 3px 6px;
    font-size: 0.8125rem;
    color: var(--tf-fg-2, #37474f);
    background: var(--tf-bg, #fff);
}
.cwe__input::placeholder {
    /* --tf-fg-5 is the DISABLED grey (2.61:1 worst surface, sub-AA by
       design) and must not paint enabled content. A placeholder is a
       hint on an ENABLED input, so it uses --tf-fg-4 ("hints, icons",
       5.18:1 worst surface) per the token scale in styles/_tokens.css. */
    color: var(--tf-fg-4, #636363);
    font-style: italic;
}
.cwe__input--hex {
    width: 9ch;
    font-family: ui-monospace, Menlo, monospace;
}
.cwe__input--code {
    width: 7ch;
    margin-left: 6px;
}
.cwe__stop-desc {
    color: var(--tf-fg-2, #37474f);
}
/* SUBTLE BUT VISIBLE (Jake, 2026-08-20): a calm pill, not a warning. This is
   the only remaining signal a thread was OUR guess rather than the user's
   decision -- Confirm is gone, so this has to read clearly without shouting.
   Shares the pill shape with .cwe__needs (border-radius + padding) but a
   neutral surface/border instead of the warning palette, so a scan down the
   column tells "we guessed" apart from "needs a human" at a glance. */
.cwe__approx {
    margin-left: 6px;
    font-size: 0.6875rem;
    color: var(--tf-fg-4, #78909c);
    background: var(--tf-surface-light, #f5f8fa);
    border: 1px solid var(--tf-border, #cfd8e3);
    border-radius: 999px;
    padding: 1px 6px;
}
.cwe__needs {
    margin-left: 6px;
    font-size: 0.6875rem;
    color: var(--tf-warning-dark, #8a5300);
    background: var(--tf-warning-soft, #fff4e5);
    border-radius: 999px;
    padding: 1px 6px;
}
.cwe__back {
    display: inline-block;
    margin-bottom: 6px;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 0.8125rem;
    color: var(--tf-primary, #119ae9);
}
.cwe__back:hover {
    text-decoration: underline;
}
/* TWO COLUMNS: stage left, colorway controls right (Jake, 2026-09-01).
   `minmax(0, 1fr)` and not `1fr` -- a grid track defaults to min-content
   width, so the wide stops table would otherwise push the stage column past
   the container instead of scrolling inside it. */
.cwe__body {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 380px;
    gap: var(--tf-sp-4, 16px);
    align-items: start;
}
.cwe__stage {
    min-width: 0;
}
.cwe__side {
    min-width: 0;
    position: sticky;
    /* Sticky so the save controls stay reachable while reading a long stop
       table -- a 56-stop design is taller than any viewport, and the whole
       point of moving these out of the flow was to stop them falling below
       the fold. */
    top: var(--tf-sp-4, 16px);
}
/* Below this the two columns are both too narrow to use: the stops table
   needs the width and the side column stops being a glanceable summary. */
@media (max-width: 1100px) {
    .cwe__body {
        grid-template-columns: minmax(0, 1fr);
    }
    .cwe__side {
        position: static;
    }
}
.cwe__saved {
    margin-top: 0;
    padding-top: 0;
}
/* The side column's own frame. The save panel used to be separated from the
   stops table by a rule above it; in a column that rule read as a divider
   between unrelated things, so the panel carries its own border instead. */
.cwe__side > .cwe__saved,
.cwe__side > .cwe__save-unavailable {
    border: 1px solid var(--tf-border, #cfd8e3);
    border-radius: var(--tf-radius-md, 12px);
    padding: var(--tf-sp-3, 12px);
    background: var(--tf-surface-light, #f5f8fa);
}
/* The name field and Save button stack in a narrow column -- side by side
   they would each be too small to use. */
.cwe__side .cwe__saved-row {
    flex-wrap: wrap;
}
.cwe__side .cwe__input--name {
    flex: 1 1 100%;
}
.cwe__saved-row {
    display: flex;
    gap: 8px;
    align-items: center;
}
.cwe__input--name {
    flex: 0 1 220px;
}
.cwe__save {
    font-size: 0.8125rem;
    padding: 4px 10px;
    border-radius: 6px;
    border: 1px solid var(--tf-primary, #119ae9);
    background: var(--tf-primary, #119ae9);
    color: #fff;
    cursor: pointer;
}
.cwe__save:disabled {
    opacity: 0.6;
    cursor: default;
}

/* Substrate declaration. Label on its own line, then ONE segmented control
   (Jake, 2026-10-21: the buttons wrapped across several lines). A 3-column
   grid has no wrapping to do: each segment is a third of the width, and
   `white-space: nowrap` plus short labels keeps the text on one line. */
.cwe__substrate {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 12px;
}
.cwe__substrate-label {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: var(--tf-text-muted, #5b6b7f);
}
.cwe__segmented {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    border: 1px solid var(--tf-border, #cfd8e3);
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
}
.cwe__substrate-opt {
    font-size: 0.8125rem;
    padding: 6px 4px;
    border: 0;
    border-left: 1px solid var(--tf-border, #cfd8e3);
    background: #fff;
    color: var(--tf-text, #1f2d3d);
    cursor: pointer;
    white-space: nowrap;
    text-align: center;
}
.cwe__substrate-opt:first-child {
    border-left: 0;
}
.cwe__substrate-opt:hover {
    background: var(--tf-surface-light, #f5f8fa);
}
.cwe__substrate-opt:focus-visible {
    outline: 2px solid var(--tf-accent, #1565c0);
    outline-offset: -2px;
}
/* The selected side is marked by a fill change AND the checkmark span in
   the template. Colour alone would leave the state invisible to anyone who
   cannot distinguish it. */
.cwe__substrate-opt--on,
.cwe__substrate-opt--on:hover {
    background: var(--tf-primary-soft, #e8f4fd);
    color: var(--tf-fg-1, #133752);
    font-weight: 600;
    box-shadow: inset 0 0 0 2px var(--tf-primary, #119ae9);
}
.cwe__substrate-check {
    margin-right: 4px;
}
.cwe__substrate-hint {
    font-size: 0.75rem;
    color: var(--tf-text-muted, #5b6b7f);
}
/* Saved colorways: one card per row, two lines. Line 1 = name + applies-to
   badge; line 2 = thread strip + Load/Delete. Nothing is squeezed onto the
   same line as the name, so the column can stay narrow without wrapping
   mid-control. */
.cwe__saved-list {
    list-style: none;
    margin: 14px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.cwe__saved-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
    border: 1px solid var(--tf-border, #cfd8e3);
    border-radius: 8px;
    background: #fff;
    padding: 8px 10px;
    font-size: 0.8125rem;
}
.cwe__saved-item--active {
    border-color: var(--tf-primary, #119ae9);
    box-shadow: inset 3px 0 0 var(--tf-primary, #119ae9);
}
.cwe__saved-top,
.cwe__saved-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
}
.cwe__saved-name {
    font-weight: 600;
    color: var(--tf-fg-1, #133752);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.cwe__saved-swatches {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    min-width: 0;
}
.cwe__saved-actions {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
}
.cwe__saved-load,
.cwe__saved-delete {
    font-size: 0.75rem;
    padding: 3px 10px;
    border-radius: 6px;
    background: #fff;
    cursor: pointer;
}
.cwe__saved-load {
    color: var(--tf-primary, #119ae9);
    border: 1px solid var(--tf-primary, #119ae9);
}
.cwe__saved-load:hover {
    background: var(--tf-primary-soft, #e8f4fd);
}
.cwe__saved-load:focus-visible,
.cwe__saved-delete:focus-visible,
.cwe__saved-target:focus-visible {
    outline: 2px solid var(--tf-accent, #1565c0);
    outline-offset: 1px;
}
/* The default-for badge. Muted, because it is a property of the colorway
   rather than an action -- it must not compete with Load/Delete. */
/* Base pill FIRST: it used to follow the role modifiers and, at equal
   specificity, silently overrode their colours. */
.cwe__saved-target {
    cursor: pointer;
    font-size: 0.6875rem;
    color: var(--tf-text-muted, #6b7a8d);
    background: var(--tf-surface-alt, #f1f4f8);
    border-radius: 999px;
    padding: 2px 10px;
    border: 1px solid var(--tf-border, #cfd8e3);
    flex-shrink: 0;
    white-space: nowrap;
}
/* THE THREE SUBSTRATE ROLES, VISUALLY DISTINCT (Jake, 2026-09-01: he found
   the pill but wanted the role obvious and clickable).

   Colours are taken from the existing semantic token set rather than minted:
   dark garments read as the dark/high-contrast chip, light garments as the
   pale warning-soft chip, and "all colors" stays neutral because it is the
   absence of a restriction. Every pair keeps its AA contrast with its own
   foreground token.

   Colour is NEVER the only signal -- each pill also carries its label text,
   so the role survives greyscale and colour-blindness. */
.cwe__saved-target--all {
    background: var(--tf-surface-dark, #e8eff5);
    color: var(--tf-fg-2, #333);
    border-color: var(--tf-border-strong, #d6d6d6);
}
.cwe__saved-target--light {
    background: var(--tf-warning-soft, #fff8e1);
    color: var(--tf-warning-fg, #b26a00);
    border-color: var(--tf-warning-fg, #b26a00);
}
.cwe__saved-target--dark {
    background: var(--tf-fg-2, #333);
    color: #fff;
    border-color: var(--tf-fg-1, #1c1c1c);
}
.cwe__saved-target:disabled {
    opacity: 0.6;
    cursor: default;
}
/* Destructive, so it reads as destructive -- but not so loud that it
   out-weighs Load in a row the user visits mainly to load from. */
.cwe__saved-delete {
    color: var(--tf-danger, #c0392b);
    border: 1px solid transparent;
}
.cwe__saved-delete:hover {
    border-color: var(--tf-danger, #c0392b);
}
.cwe__saved-delete:disabled {
    color: var(--tf-text-muted, #6b7a8d);
    cursor: default;
}
.cwe__save-unavailable {
    margin: 14px 0 0;
    padding-top: 12px;
    border-top: 1px solid var(--tf-border, #cfd8e3);
}
.cwe__actions {
    /* Keeps the two buttons together: they sit beside the thread fields when
       there is room and wrap as a pair, with a gap, when the column is
       narrow -- never a stray word dangling off the inputs. */
    display: inline-flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-left: 8px;
    vertical-align: middle;
}
.cwe__action {
    /* The two actions beside a stop's thread (Jake, 2026-10-27: "the links to
       the right of each color ... don't look like buttons"). Same button
       language as the stop-number button (.cwe__highlight): bordered, filled,
       a small shadow, hover and focus-visible states. Deliberately NOT the
       pill language (.cwe__approx / .cwe__needs: 999px radius, tiny muted
       text, no shadow) -- pills are STATUS, these are things you press. */
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 32px;
    padding: 4px 12px;
    border: 1px solid var(--tf-border, #cfd8e3);
    border-radius: 6px;
    background: var(--tf-surface-light, #f5f8fa);
    color: var(--tf-fg-1, #133752);
    font: inherit;
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
    vertical-align: middle;
    cursor: pointer;
    box-shadow: 0 1px 1px rgba(19, 55, 82, 0.12);
    transition: background 0.12s, border-color 0.12s, box-shadow 0.12s;
}
.cwe__action:hover {
    border-color: var(--tf-accent, #1565c0);
    background: var(--tf-accent-bg, rgba(21, 101, 192, 0.07));
    color: var(--tf-accent, #1565c0);
}
.cwe__action:active {
    box-shadow: none;
    transform: translateY(1px);
}
.cwe__action:focus-visible {
    outline: 2px solid var(--tf-accent, #1565c0);
    outline-offset: 2px;
}
/* Primary-weight: the forward action. Filled with the accent colour. */
.cwe__browse {
    background: var(--tf-accent, #1565c0);
    border-color: var(--tf-accent, #1565c0);
    color: #fff;
}
.cwe__browse:hover {
    background: var(--tf-accent-dark, #0d47a1);
    border-color: var(--tf-accent-dark, #0d47a1);
    color: #fff;
}
/* Secondary-weight: the way back. Outlined, accent text. */
.cwe__revert {
    background: #fff;
    border-color: var(--tf-accent, #1565c0);
    color: var(--tf-accent, #1565c0);
}
/* Thread picker FLYOVER (Jake, 2026-08-20: "it should be a flyover on the
   right sort of like the store preview in the store editor"). Mirrors
   StoreEditShell.vue's `.ces-preview-*` drawer exactly: fixed scrim, right-
   anchored panel translated off-screen while closed, transform transition
   in. Class names are this component's own (`cwe__picker-*`) rather than
   reused `ces-*` ones, because that block is scoped to the store editor.
   z-index sits below HelpFlyover's 120/121 (same reasoning as that
   component's own comment: help opened from within this editor should still
   land on top of it) and above the page's normal content. */
.cwe__picker-scrim {
    position: fixed;
    inset: 0;
    background: rgba(19, 55, 82, 0.42);
    backdrop-filter: blur(2px);
    z-index: 90;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.25s;
}
.cwe__picker-scrim.open {
    opacity: 1;
    pointer-events: auto;
}
.cwe__picker-drawer {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: min(420px, 92vw);
    background: var(--tf-bg, #fff);
    z-index: 91;
    box-shadow: -12px 0 40px rgba(19, 55, 82, 0.18);
    transform: translateX(100%);
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    flex-direction: column;
}
.cwe__picker-drawer.open {
    transform: translateX(0);
}
.cwe__picker-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid var(--tf-border, #cfd8e3);
    background: var(--tf-surface-muted, #f5f8fa);
    font-size: 0.8125rem;
    font-weight: 600;
    flex-shrink: 0;
}
.cwe__picker-close {
    font-size: 0.75rem;
    font-weight: 400;
    color: var(--tf-primary, #119ae9);
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
}
.cwe__picker-body {
    flex: 1;
    min-height: 0;
    /* The PICKER owns the scrolling now (its results grid flexes to fill the
       drawer), so this wrapper must not also scroll -- two nested scroll
       containers put the scrollbar on the outer one and re-trap the grid at
       its natural height, which is the bug this pair of changes fixes. */
    overflow: hidden;
    padding: 12px 16px;
    /* A flex column so `height: 100%` on .tcp resolves against a real
       height rather than `auto`. */
    display: flex;
    flex-direction: column;
}
/* The replace question. Tinted rather than red: this is a decision to make,
   not a failure that happened. */
.cwe__replace {
    margin-top: 10px;
    padding: 10px;
    border: 1px solid var(--tf-warning-fg, #b26a00);
    border-radius: 8px;
    background: var(--tf-warning-soft, #fff8e1);
}
.cwe__replace-q {
    margin: 0 0 8px;
    font-size: 0.8125rem;
    color: var(--tf-fg-2, #333);
}
.cwe__replace-actions {
    display: flex;
    gap: 8px;
    align-items: center;
}
.cwe__replace-cancel {
    font-size: 0.8125rem;
    padding: 4px 10px;
    border-radius: 6px;
    border: 1px solid var(--tf-border, #cfd8e3);
    background: #fff;
    color: var(--tf-fg-2, #333);
    cursor: pointer;
}
.cwe__preview {
    margin: 14px 0 0;
}
.cwe__preview img {
    /* Wilcom returns a 1:1 PHYSICAL-size PNG (mm/25.4 x dpi), so an 89mm
       logo at 96dpi is only ~336px and looked tiny in a full-width panel.
       We now request 200dpi from the API and let the image fill the width,
       capped so a large design does not dominate the page. */
    width: 100%;
    max-width: 640px;
    height: auto;
    border: 1px solid var(--tf-border, #cfd8e3);
    border-radius: 8px;
    background: var(--tf-surface-light, #f5f8fa);
    padding: 8px;
}
</style>
